import { providerFor } from './utilityProviders';

// Lease render config (matches lease_render/render_lease.py's expected shape).
// Builds defaults from an AppFolio application group, then layers user edits
// on top. The form panel mutates this state; the preview pane sends it back
// to /api/lease/preview to get HTML.
//
// Money fields in LeaseFormState are integer CENTS (§C.8). Conversion to
// dollars happens only at the render boundary via formatMoneyCents().
// ApplicationGroupShape from AppFolio stays in DOLLARS on the wire (§B
// Decision 15); buildDefaultFormState() is the single conversion boundary.

import { ymd, addMonths, computeEndDateFromTerm, computeTermFromEndDate, diffDays } from './date';
import { dollarsNumberToCents, formatMoneyCents } from './money';

/**
 * §30 builder constraint 2: every renewal is a successive fixed term of ≥6
 * months. MIRRORS the API source of truth — `MIN_RENEWAL_TERM_MONTHS` in
 * apps/api/src/lib/renewal.ts (the SERVER hard-block is authoritative; this
 * UI-side copy is a pre-submit UX guard only). Keep the two in sync.
 */
export const MIN_RENEWAL_TERM_MONTHS = 6;

// ---------------------------------------------------------------------------
// Type aliases
// ---------------------------------------------------------------------------

export type PropertyType = 'mfr' | 'sfr';
export type AnimalChoice = 'no' | 'yes';
export type ParkingChoice = 'none' | 'unassigned' | 'assigned';
export type YardCare = 'landlord' | 'resident';

/**
 * For resident-responsible yard care, HOW the responsibility is fulfilled:
 * 'resident_maintains' (default) — the resident does it themselves;
 * 'lessor_arranges' — the resident elected to have Lessor arrange it and bill them.
 * Collected pre-lease (intake) or set by staff; baked into the lease (no
 * signing-time field).
 */
export type YardCareArrangement = 'resident_maintains' | 'lessor_arranges';

/**
 * The YARD CARE arrangement line for `{{ yard_care_arrangement }}` (resident-
 * responsible section only). Silent (empty) when the resident maintains the yard
 * themselves — the default; the section's responsibilities prose stands on its
 * own. A single sentence when the resident elected to have Lessor arrange it and
 * bill their account. Replaces the old signing-time DocuSeal select so residents
 * fill nothing in the lease.
 */
export function buildYardCareArrangement(
  arrangement: YardCareArrangement | undefined,
): string {
  if (arrangement === 'lessor_arranges') {
    return (
      "Resident has elected to have Lessor arrange yard care and landscaping " +
      "services on Resident's behalf, and authorizes Lessor to bill the cost of " +
      "those services to Resident's account. Resident remains responsible for the " +
      "cost of maintaining the yard, lawn, and landscaping in the condition provided."
    );
  }
  return '';
}
export type UtilityState = 'tenant_direct' | 'landlord_billed_full' | 'landlord_provided' | 'ratio';

/**
 * Resident designation carried on the lease form-state (pre-lease intake
 * wiring). Populated from the household's designation intake, or entered by
 * staff in the builder to satisfy the designation hard-block. When present, the
 * RESIDENT DESIGNATION section renders these details instead of blank DocuSeal
 * fill fields. Absent/empty → the original blank-fill section renders (the
 * tenant fills it at signing). `filledBy` records who provided it ('staff'
 * satisfies the block even when the tenant never responded).
 */
export interface ResidentDesignation {
  designeeName?: string;
  relationship?: string;
  /** Single mailing-address line (intake collects no address; staff may add one). */
  mailingAddress?: string;
  phone?: string;
  email?: string;
  filledBy?: 'tenant' | 'staff';
}

// ---------------------------------------------------------------------------
// §5: UtilityConfig
// ---------------------------------------------------------------------------

export interface UtilityConfig {
  name: string;
  state: UtilityState;
  /** Only relevant when state === 'ratio'. Stored even when not active. */
  shareNumerator?: number;
  /** Only relevant when state === 'ratio'. Stored even when not active. */
  shareDenominator?: number;
}

// ---------------------------------------------------------------------------
// §4: OtherCharge
// ---------------------------------------------------------------------------

export interface OtherCharge {
  /** Charge type. 'custom' requires `customLabel` for the §2 row label. */
  type: 'storage' | 'washer_dryer' | 'custom';
  /** Required when type === 'custom'. User-entered label for the §2 row. */
  customLabel?: string;
  /** Integer cents (§C.8). */
  amountCents: number;
}

/** Display label for an OtherCharge row (used by §2 and the financial summary). */
export function otherChargeLabel(r: OtherCharge): string {
  if (r.type === 'storage') return 'Storage';
  if (r.type === 'washer_dryer') return 'Washer Dryer';
  return (r.customLabel ?? '').trim() || 'Other';
}

// ---------------------------------------------------------------------------
// §9: PaymentPlan types
// ---------------------------------------------------------------------------

/**
 * A single scheduled payment in a payment plan (§9.5).
 *
 * Shape diff from Phase A placeholder:
 *   - renamed `dueDate` → `chargeDate` (matches standalone form's "Charge Date" header)
 *   - added `paymentNumber` (1-based; internal use only — does not appear in PDF)
 */
export interface ComputedPayment {
  /** 1-based payment number. Internal use only — not rendered in the PDF schedule table. */
  paymentNumber: number;
  /** yyyy-mm-dd; rendered as "Charge Date" in the schedule table per §9.2. */
  chargeDate: string;
  /** Integer cents (§C.8); formatted as dollars-and-cents at the display boundary. */
  amount: number;
}

/**
 * Payment plan configuration stored in LeaseFormState (§9.5).
 *
 * Shape diff from Phase A placeholder:
 *   - renamed `firstPaymentDate` → `firstChargeDate` (matches standalone "Charge Date" header)
 *   - added `depositOnPlan` (cents; 0..depositTotal; staff-entered amount of deposit on plan)
 *   - added `includeCarpetFee` (boolean; all-or-nothing carpet fee inclusion)
 *   - dropped `payments[]` (computed, not stored; use computePaymentSchedule() at render time)
 */
export interface PaymentPlanConfig {
  /** Amount of the Refundable Security Deposit on the plan, in cents. 0..depositTotal. */
  depositOnPlan: number;
  /** When true, the full carpetCleaningFee (cents) is added to the plan. All-or-nothing. */
  includeCarpetFee: boolean;
  /** Number of payments (§9.5: 2..6). */
  numberOfPayments: number;
  /** First charge date (yyyy-mm-dd). Defaults per §9.7.1; staff can override. */
  firstChargeDate: string;
  /** Interval between charges in months. Default 1. */
  intervalMonths: number;
  /**
   * Cents of the carpet cleaning fee on the plan (0..fee). Supersedes
   * `includeCarpetFee`; absent on plans saved before 2026-10-08 (then
   * `includeCarpetFee ? fullFee : 0`). See carpetFeeOnPlanCents.
   */
  carpetFeeOnPlan?: number;
  /** One-time fee id → cents of that fee on the plan (0..fee). */
  feesOnPlan?: Record<string, number>;
  /**
   * The tenant requested the plan in writing (RCW 59.18.610): the schedule is
   * set by statute (computeLeasePaymentSchedule) and numberOfPayments /
   * firstChargeDate / intervalMonths are ignored.
   */
  tenantWrittenRequest?: boolean;
}

// ---------------------------------------------------------------------------
// Payment plan constants (§9.5)
// ---------------------------------------------------------------------------

export const PAYMENT_PLAN_MIN_PAYMENTS = 2;
export const PAYMENT_PLAN_MAX_PAYMENTS = 6;
export const PAYMENT_PLAN_DEFAULT_INTERVAL_MONTHS = 1;

/**
 * Maximum allowed span of a payment-plan schedule, in months (§9; WEL-35).
 * The span from the first charge to the last charge must not exceed this — a
 * plan can't run longer than a (default) 12-month lease.
 */
export const PAYMENT_PLAN_MAX_SPAN_MONTHS = 12;

/**
 * Months elapsed from the first charge to the last charge of a payment plan.
 * The schedule steps `intervalMonths` between each of the `numberOfPayments`
 * charges (see computePaymentSchedule), so the last charge lands
 * `(numberOfPayments - 1) * intervalMonths` months after the first. Used by the
 * UI duration cap and validateLeaseState; agrees with the schedule preview.
 */
export function paymentPlanSpanMonths(
  plan: Pick<PaymentPlanConfig, 'numberOfPayments' | 'intervalMonths'>,
): number {
  return Math.max(0, plan.numberOfPayments - 1) * plan.intervalMonths;
}

// ---------------------------------------------------------------------------
// §9.7.1: defaultFirstChargeDate — first-of-next-month with 20-day runway
// ---------------------------------------------------------------------------

/**
 * Compute the default first Charge Date for a payment plan given the lease
 * start date (§9.7.1 algorithm verbatim).
 *
 * Algorithm: use "first of next month" unless there are fewer than 20 days
 * between the lease start and that date — if so, push to the first of the
 * month after that, giving the resident enough runway to fund the first
 * installment.
 *
 * JSDoc test cases (§9.7.1 example table):
 *   defaultFirstChargeDate('2026-01-05') === '2026-02-01'  // 27 days → next month
 *   defaultFirstChargeDate('2026-01-12') === '2026-02-01'  // 20 days → next month (boundary)
 *   defaultFirstChargeDate('2026-01-13') === '2026-03-01'  // 19 days → month after next
 *   defaultFirstChargeDate('2026-01-31') === '2026-03-01'  // 1 day   → month after next
 *   defaultFirstChargeDate('2026-02-01') === '2026-03-01'  // 28 days → next month
 *   defaultFirstChargeDate('2026-02-12') === '2026-04-01'  // 17 days → month after next
 */
export function defaultFirstChargeDate(leaseStartYmd: string): string {
  const start = new Date(leaseStartYmd + 'T00:00:00');
  const firstOfNextMonth = new Date(start.getFullYear(), start.getMonth() + 1, 1);
  const msPerDay = 1000 * 60 * 60 * 24;
  const daysUntilNextFirst = Math.floor(
    (firstOfNextMonth.getTime() - start.getTime()) / msPerDay
  );
  if (daysUntilNextFirst < 20) {
    return ymd(new Date(start.getFullYear(), start.getMonth() + 2, 1));
  }
  return ymd(firstOfNextMonth);
}

// ---------------------------------------------------------------------------
// §9.7.2: computePaymentSchedule — cents math with remainder in last payment
// ---------------------------------------------------------------------------

/**
 * Compute the payment schedule for a payment plan (§9.7.2 verbatim).
 *
 * All arithmetic is integer cents (no float conversion). The first
 * (numberOfPayments - 1) payments each receive Math.floor(total / n); the
 * last payment absorbs the remainder so that sum(amounts) === planTotalCents
 * exactly. Uses addMonths() from date.ts for monthly date stepping.
 *
 * Invariant: sum(p.amount for p in result) === plan.depositOnPlan +
 *   (plan.includeCarpetFee ? carpetCleaningFeeCents : 0).
 *
 * JSDoc test cases (manual smoke per Phase A spec gap #7):
 *
 *   Case 1 — deposit-only, 3 payments, $1300.00 deposit (130000 cents):
 *     base = floor(130000/3) = 43333; remainder = 130000 - 43333*3 = 1
 *     → [43333, 43333, 43334]  sum = 130000 ✓
 *
 *   Case 2 — deposit + carpet, 3 payments, $1300.00 + $350.00 = $1650.00 (165000 cents):
 *     base = floor(165000/3) = 55000; remainder = 0
 *     → [55000, 55000, 55000]  sum = 165000 ✓
 *
 *   Case 3 — rounding edge, 3 payments, $1000.01 = 100001 cents:
 *     base = floor(100001/3) = 33333; remainder = 100001 - 33333*3 = 2
 *     → [33333, 33333, 33335]  sum = 100001 ✓
 *
 *   Case 4 — 6 payments, $100.00 = 10000 cents:
 *     base = floor(10000/6) = 1666; remainder = 10000 - 1666*6 = 4
 *     → [1666, 1666, 1666, 1666, 1666, 1670]  sum = 10000 ✓
 *
 *   Case 5 — day-of-month clamp, firstChargeDate='2026-01-31', 2 payments, 1 month interval:
 *     payment 0: chargeDate='2026-01-31'
 *     payment 1: chargeDate=addMonths('2026-01-31',1) = '2026-02-28' (clamp)
 *     → clamping works as expected ✓
 *
 *   Case 6 — partial deposit on plan, 2 payments, depositOnPlan=50000, no carpet:
 *     planTotal = 50000; base = 25000; remainder = 0
 *     → [25000, 25000]  sum = 50000 ✓
 */
export function computePaymentSchedule(
  plan: PaymentPlanConfig,
  carpetCleaningFeeCents: number
): ComputedPayment[] {
  // All inputs are integer cents per §C.8; no float conversion anywhere.
  return schedulePlanTotal(plan, plan.depositOnPlan + carpetFeeOnPlanCents(plan, carpetCleaningFeeCents));
}

/** The agreed-plan schedule for a plan total: equal payments from the first charge date, remainder last. */
function schedulePlanTotal(plan: PaymentPlanConfig, planTotalCents: number): ComputedPayment[] {
  const base = Math.floor(planTotalCents / plan.numberOfPayments);
  const remainder = planTotalCents - base * plan.numberOfPayments;
  return Array.from({ length: plan.numberOfPayments }, (_, i) => ({
    paymentNumber: i + 1,
    chargeDate: addMonths(plan.firstChargeDate, plan.intervalMonths * i),
    amount: i === plan.numberOfPayments - 1 ? base + remainder : base,
  }));
}

// ---------------------------------------------------------------------------
// WEL-89: effectiveCarpetFee — the carpet fee that actually applies to a lease
// ---------------------------------------------------------------------------

/**
 * WEL-89: the carpet cleaning fee that ACTUALLY applies to this lease.
 *
 * When the unit has carpet (`hasCarpet !== false`, the default) this is the raw
 * `carpetCleaningFee`. When carpet is toggled OFF the CARPET section is dropped
 * entirely, so its fee MUST be zero everywhere it flows — the financial summary
 * "Carpet fee due at lease start" row, the `carpet_cleaning_fee` /
 * `non_refundable_fees` variables, the payment-plan total + schedule, the
 * inclusion bullet, and the `[[REF:carpet_fee]]` cross-ref. A stale non-zero
 * `carpetCleaningFee` left over from before the toggle was flipped off must not
 * resurrect any of those. Every read of the carpet fee in builder math goes
 * through this so a hidden field can never leak money into the document.
 */
export function effectiveCarpetFee(s: LeaseFormState): number {
  return s.hasCarpet !== false ? s.carpetCleaningFee : 0;
}

// ---------------------------------------------------------------------------
// Move-In Payment Plan + SUMMARY OF AMOUNTS DUE (docs/move-in-payment-plan-plan.md)
// ---------------------------------------------------------------------------
//
// The deposit is due upon signing; every other move-in amount is due on or
// before the move-in date. A plan may carry any part of the deposit, of the
// carpet cleaning fee, and of each staff-added one-time fee (RCW 59.18.610 lets
// a tenant ask for exactly that). Staff decide what and how much; when the
// tenant asked in writing, the schedule follows the statute (3 equal monthly
// payments, 2 when the lease is under 3 months, the first at move-in, the rest
// on the following 1sts — "due at the same time as rent is due").
//
// ONE model (computeAmountsDue) feeds the lease's summary + plan sections, the
// builder preview, and the countersigner review card, so they can never differ.

/** A staff-added nonrefundable one-time move-in fee. */
export interface OneTimeFee {
  /** Stable id; keys `PaymentPlanConfig.feesOnPlan`. */
  id: string;
  /** Staff-entered name, e.g. "Key fee". */
  name: string;
  /** Integer cents. */
  amountCents: number;
}

/**
 * A fee name as it may appear in the lease: one line, no Markdown, table or tag
 * characters. `&` becomes "and" so no HTML entity (`&lcub;` → `{`) can be spelled
 * that the Markdown renderer would decode into a DocuSeal field tag.
 */
export function cleanFeeName(name: string | null | undefined): string {
  return (name ?? '')
    .replace(/&/g, ' and ')
    .replace(/[\r\n|{}<>[\]*_`#\\]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 60);
}

/** The one-time fees that actually apply: named, with a positive amount. */
export function effectiveOneTimeFees(s: Pick<LeaseFormState, 'oneTimeFees'>): OneTimeFee[] {
  return (Array.isArray(s.oneTimeFees) ? s.oneTimeFees : []).filter(
    (f): f is OneTimeFee =>
      !!f &&
      typeof f.id === 'string' &&
      cleanFeeName(f.name) !== '' &&
      typeof f.amountCents === 'number' &&
      Number.isFinite(f.amountCents) &&
      f.amountCents > 0,
  );
}

/**
 * The singular Refundable Security Deposit (base + per-animal contribution for
 * chargeable animals), the same total the deposit section prints.
 */
export function refundableDepositTotalCents(s: LeaseFormState): number {
  const animalDeposit =
    s.animals === 'yes' ? chargeableAnimalCount(s) * s.animalDepositPerAnimal : 0;
  return s.securityDeposit + animalDeposit;
}

/** Carpet fee cents on a plan. A legacy plan carries only `includeCarpetFee` (all or nothing). */
export function carpetFeeOnPlanCents(plan: PaymentPlanConfig, carpetFeeCents: number): number {
  const raw =
    typeof plan.carpetFeeOnPlan === 'number' && Number.isFinite(plan.carpetFeeOnPlan)
      ? plan.carpetFeeOnPlan
      : plan.includeCarpetFee
        ? carpetFeeCents
        : 0;
  return Math.max(0, Math.min(carpetFeeCents, raw));
}

function clampCents(n: unknown, max: number): number {
  return typeof n === 'number' && Number.isFinite(n) ? Math.max(0, Math.min(max, n)) : 0;
}

export type MoveInChargeKind = 'deposit' | 'carpet' | 'fee';

/** One one-time move-in charge and how much of it is on the plan. */
export interface MoveInCharge {
  /** 'deposit' | 'carpet' | `fee:<id>` */
  key: string;
  kind: MoveInChargeKind;
  /** Plain words for the summary tables. */
  label: string;
  fullCents: number;
  /** 0 when there is no plan. */
  onPlanCents: number;
}

/** Every one-time move-in charge on the lease (deposit, carpet fee, one-time fees), with its plan share. */
export function moveInCharges(s: LeaseFormState): MoveInCharge[] {
  const plan = s.paymentPlanEnabled && s.paymentPlan ? s.paymentPlan : null;
  const out: MoveInCharge[] = [];
  const deposit = refundableDepositTotalCents(s);
  if (deposit > 0) {
    out.push({
      key: 'deposit',
      kind: 'deposit',
      label: 'Refundable security deposit',
      fullCents: deposit,
      onPlanCents: plan ? clampCents(plan.depositOnPlan, deposit) : 0,
    });
  }
  const carpet = effectiveCarpetFee(s);
  if (carpet > 0) {
    out.push({
      key: 'carpet',
      kind: 'carpet',
      label: 'Carpet cleaning fee',
      fullCents: carpet,
      onPlanCents: plan ? carpetFeeOnPlanCents(plan, carpet) : 0,
    });
  }
  for (const f of effectiveOneTimeFees(s)) {
    out.push({
      key: `fee:${f.id}`,
      kind: 'fee',
      label: cleanFeeName(f.name),
      fullCents: f.amountCents,
      onPlanCents: plan ? clampCents(plan.feesOnPlan?.[f.id], f.amountCents) : 0,
    });
  }
  return out;
}

/**
 * Return the total cents amount on the payment plan. 0 when paymentPlanEnabled
 * is false or paymentPlan is null.
 *
 * WEL-89: the carpet share reads effectiveCarpetFee (via moveInCharges), so an
 * off-carpet lease never counts a carpet fee toward the plan total.
 */
export function getPlanTotal(s: LeaseFormState): number {
  if (!s.paymentPlanEnabled || !s.paymentPlan) return 0;
  return moveInCharges(s).reduce((sum, c) => sum + c.onPlanCents, 0);
}

/** RCW 59.18.610(2): 3 monthly payments for a tenancy of 3 months or longer, otherwise 2. */
export function statutoryPaymentCount(leaseTermMonths: number): number {
  return Number.isFinite(leaseTermMonths) && leaseTermMonths >= 3 ? 3 : 2;
}

/** Equal integer-cent payments, the remainder in the last (computePaymentSchedule's rule). */
function splitPlanCents(totalCents: number, n: number): number[] {
  const base = Math.floor(totalCents / n);
  const remainder = totalCents - base * n;
  return Array.from({ length: n }, (_, i) => (i === n - 1 ? base + remainder : base));
}

/**
 * The lease's plan schedule. An agreed plan steps from its first charge date by
 * its interval (computePaymentSchedule). A plan the tenant requested in writing
 * follows RCW 59.18.610: payment 1 on the move-in date, the rest on the 1st of
 * each following month. Empty when there is no plan or nothing on it.
 */
export function computeLeasePaymentSchedule(s: LeaseFormState): ComputedPayment[] {
  if (!s.paymentPlanEnabled || !s.paymentPlan) return [];
  const total = getPlanTotal(s);
  if (total <= 0) return [];
  const plan = s.paymentPlan;
  if (plan.tenantWrittenRequest) {
    const n = statutoryPaymentCount(s.leaseTermMonths);
    const start = new Date(s.leaseStartDate + 'T00:00:00');
    const nextFirst = ymd(new Date(start.getFullYear(), start.getMonth() + 1, 1));
    return splitPlanCents(total, n).map((amount, i) => ({
      paymentNumber: i + 1,
      chargeDate: i === 0 ? s.leaseStartDate : addMonths(nextFirst, i - 1),
      amount,
    }));
  }
  return schedulePlanTotal(plan, total);
}

export interface AmountsDueRow {
  /** 'less' rows are subtracted; 'plan_payment' is a plan payment due at move-in. */
  kind: 'charge' | 'less' | 'plan_payment';
  /** Plain words (no section references). */
  label: string;
  /** Always positive. */
  cents: number;
}

export interface AmountsDueList {
  /** 'signing' = due upon signing; 'move_in' = due on or before move-in. */
  when: 'signing' | 'move_in';
  heading: string;
  rows: AmountsDueRow[];
  subtotalCents: number;
}

export interface AmountsDue {
  /** One list when the lease starts on or before its document date, else two. */
  lists: AmountsDueList[];
  /** Total due on or before move-in. */
  totalCents: number;
  charges: MoveInCharge[];
  planTotalCents: number;
  schedule: ComputedPayment[];
  /** Plan payments dated on or before move-in: counted in the total. */
  paymentsAtMoveIn: ComputedPayment[];
  /** Plan payments after move-in: owed in addition to the total. */
  paymentsAfterMoveIn: ComputedPayment[];
}

/**
 * What is due before move-in, and when. The deposit is due upon signing; the
 * first month's rent, the other monthly charges, the carpet fee and the
 * one-time fees are due on or before the move-in date. A charge with money on
 * the plan shows in full with a "Less" row under it; a plan payment dated on
 * or before move-in (a written-request plan's first) is added to the list.
 * `effectiveHoaFeeCents` is resolved by the caller (the HOA default is async).
 */
export function computeAmountsDue(s: LeaseFormState, effectiveHoaFeeCents: number): AmountsDue {
  const charges = moveInCharges(s);
  const planTotalCents = getPlanTotal(s);
  const schedule = computeLeasePaymentSchedule(s);
  const moveIn = s.leaseStartDate ?? '';
  const paymentsAtMoveIn = schedule.filter((p) => moveIn !== '' && p.chargeDate <= moveIn);
  const paymentsAfterMoveIn = schedule.filter((p) => !(moveIn !== '' && p.chargeDate <= moveIn));

  const chargeRows = (c: MoveInCharge): AmountsDueRow[] => [
    { kind: 'charge', label: c.label, cents: c.fullCents },
    ...(c.onPlanCents > 0
      ? [{ kind: 'less' as const, label: 'Less: on the Move-In Payment Plan', cents: c.onPlanCents }]
      : []),
  ];

  // First month's rent and the other monthly charges, due at move-in.
  const monthlyRows: AmountsDueRow[] = [];
  if (s.monthlyRent > 0) monthlyRows.push({ kind: 'charge', label: "First month's rent", cents: s.monthlyRent });
  const monthlyAnimalRent =
    s.animals === 'yes' ? chargeableAnimalCount(s) * s.animalRentPerAnimal : 0;
  if (monthlyAnimalRent > 0) {
    monthlyRows.push({ kind: 'charge', label: 'Monthly animal rent', cents: monthlyAnimalRent });
  }
  if (s.parkingCharged && (s.parkingChargeCents ?? 0) > 0) {
    monthlyRows.push({ kind: 'charge', label: 'Parking', cents: s.parkingChargeCents ?? 0 });
  }
  for (const r of s.otherCharges ?? []) {
    if (r.amountCents <= 0) continue;
    monthlyRows.push({ kind: 'charge', label: otherChargeLabel(r), cents: r.amountCents });
  }
  if (effectiveHoaFeeCents > 0) {
    monthlyRows.push({ kind: 'charge', label: 'HOA Fee', cents: effectiveHoaFeeCents });
  }

  const n = schedule.length;
  const planPaymentRows: AmountsDueRow[] = paymentsAtMoveIn.map((p) => ({
    kind: 'plan_payment',
    label: `Move-In Payment Plan, payment ${p.paymentNumber} of ${n}`,
    cents: p.amount,
  }));

  const depositRows = charges.filter((c) => c.kind === 'deposit').flatMap(chargeRows);
  const feeRows = charges.filter((c) => c.kind !== 'deposit').flatMap(chargeRows);
  const moveInHeading = moveIn
    ? `Due on or before move-in, ${fmtDateLong(moveIn)}`
    : 'Due on or before move-in';

  const subtotal = (rows: AmountsDueRow[]): number =>
    rows.reduce((sum, r) => sum + (r.kind === 'less' ? -r.cents : r.cents), 0);
  const list = (when: AmountsDueList['when'], heading: string, rows: AmountsDueRow[]): AmountsDueList => ({
    when,
    heading,
    rows,
    subtotalCents: subtotal(rows),
  });

  // One list when the lease starts on (or before) the day it is prepared —
  // signing and move-in are the same occasion — or when nothing is due upon
  // signing (the whole deposit is on the plan; a "$0.00 due upon signing" list
  // would only confuse). Otherwise the deposit is due upon signing.
  const sameOccasion = !!moveIn && !!s.documentDate && moveIn <= s.documentDate;
  const lists: AmountsDueList[] =
    sameOccasion || subtotal(depositRows) <= 0
      ? [list('move_in', moveInHeading, [...depositRows, ...monthlyRows, ...feeRows, ...planPaymentRows])]
      : [
          list('signing', 'Due upon signing this Agreement', depositRows),
          list('move_in', moveInHeading, [...monthlyRows, ...feeRows, ...planPaymentRows]),
        ];

  return {
    lists,
    totalCents: lists.reduce((sum, l) => sum + l.subtotalCents, 0),
    charges,
    planTotalCents,
    schedule,
    paymentsAtMoveIn,
    paymentsAfterMoveIn,
  };
}

function amountsDueMoney(r: AmountsDueRow): string {
  return r.kind === 'less' ? `−${formatMoneyCents(r.cents)}` : formatMoneyCents(r.cents);
}

/** The SUMMARY OF AMOUNTS DUE lists (`### heading` + table each). */
export function buildAmountsDueSections(m: AmountsDue): string {
  return m.lists
    .map((l) => {
      const subtotal =
        m.lists.length > 1 && l.rows.length > 1
          ? [`| **Subtotal** | **${formatMoneyCents(l.subtotalCents)}** |`]
          : [];
      return [
        `### ${l.heading}`,
        '',
        '| Item | Amount |',
        '|---|---:|',
        ...l.rows.map((r) => {
          const label = r.kind === 'less' ? `${r.label} ([[REF:payment_plan]])` : r.label;
          return `| ${label} | ${amountsDueMoney(r)} |`;
        }),
        ...subtotal,
      ].join('\n');
    })
    .join('\n\n');
}

/** The charcoal total bar. */
export function buildTotalDueGrandTotal(totalCents: number): string {
  return (
    '<div class="grand-total">' +
    '<span class="gt-label">Total due on or before move-in</span>' +
    `<span class="gt-amount">${formatMoneyCents(totalCents)}</span>` +
    '</div>'
  );
}

/** "A and B" / "A, B, and C". */
function joinAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** The sentence under the total: the plan payments are owed IN ADDITION to it, after move-in. */
export function buildPaymentPlanSummaryParagraph(m: AmountsDue): string {
  if (m.paymentsAfterMoveIn.length === 0) return '';
  const dates = joinAnd(
    m.paymentsAfterMoveIn.map((p) => `${formatMoneyCents(p.amount)} on ${fmtDateLong(p.chargeDate)}`),
  );
  const lead =
    m.paymentsAtMoveIn.length > 0
      ? 'Payment 1 of the Move-In Payment Plan is included in this total. The remaining payments are not part of this total and are not due before move-in.'
      : 'The Move-In Payment Plan amounts are not part of this total and are not due before move-in.';
  return `${lead} Resident owes them in addition to this total, on the dates in [[REF:payment_plan]]: ${dates}.`;
}

/** The MOVE-IN PAYMENT PLAN section's variable parts. */
export function buildPaymentPlanSectionParts(
  s: LeaseFormState,
  m: AmountsDue,
): { inclusions: string; scheduleIntro: string; scheduleRows: string; closing: string } {
  const onlyMoveInList = m.lists.length === 1;
  const ref: Record<MoveInChargeKind, string> = {
    deposit: '[[REF:security_deposit]]',
    carpet: '[[REF:carpet_fee]]',
    fee: '[[REF:move_in_fees]]',
  };
  const inclusions = m.charges
    .filter((c) => c.onPlanCents > 0)
    .map((c) => {
      const what =
        c.kind === 'deposit'
          ? 'The Refundable Security Deposit'
          : c.kind === 'carpet'
            ? 'The nonrefundable carpet cleaning fee'
            : `The nonrefundable ${c.label}`;
      const upFront = c.fullCents - c.onPlanCents;
      const when =
        c.kind === 'deposit' && !onlyMoveInList ? 'upon signing this Agreement' : 'on or before move-in';
      const split =
        upFront > 0
          ? `${formatMoneyCents(upFront)} is due ${when}; the remaining ${formatMoneyCents(c.onPlanCents)} is paid under this plan.`
          : 'all of it is paid under this plan.';
      return `- ${what} described in ${ref[c.kind]}, ${formatMoneyCents(c.fullCents)} in full: ${split}`;
    })
    .join('\n');

  const requested = s.paymentPlan?.tenantWrittenRequest ? 'Resident requested this plan in writing. ' : '';
  const scheduleIntro =
    m.paymentsAtMoveIn.length > 0
      ? `${requested}Payment 1 is due at move-in and is included in the move-in total in [[REF:amounts_due]]; each later payment is due on the date shown, in addition to that total and to rent:`
      : `${requested}Each payment below is due on the date shown, in addition to the move-in total in [[REF:amounts_due]] and to rent:`;

  const scheduleRows = m.schedule
    .map((p) => `| ${fmtDateLong(p.chargeDate)} | ${formatMoneyCents(p.amount)} |`)
    .join('\n');

  const closing: string[] = [];
  if (m.charges.some((c) => c.kind === 'deposit' && c.onPlanCents > 0)) {
    closing.push(
      'All other terms of [[REF:security_deposit]] (SECURITY / DAMAGE / CLEANING DEPOSIT) of this Agreement apply to the deposit amount paid under this plan, including its use, accounting, and return. The Refundable Security Deposit is considered fully paid only when all scheduled amounts above have been received by Lessor.',
    );
  }
  if (m.charges.some((c) => c.kind !== 'deposit' && c.onPlanCents > 0)) {
    closing.push('A fee paid under this plan remains nonrefundable.');
  }
  return { inclusions, scheduleIntro, scheduleRows, closing: closing.join('\n\n') };
}

/** The MOVE-IN FEES section body; empty when the lease has no one-time fees. */
export function buildMoveInFeesBody(m: AmountsDue): string {
  const fees = m.charges.filter((c) => c.kind === 'fee');
  if (fees.length === 0) return '';
  const except = fees.some((c) => c.onPlanCents > 0)
    ? ', except for the amounts scheduled under the Move-In Payment Plan in [[REF:payment_plan]]'
    : '';
  return [
    `Resident shall pay the following one-time fees in addition to, and not as part of, the security deposit. These fees are nonrefundable and are due on or before move-in${except}:`,
    '',
    ...fees.map((c) => `- ${c.label}: ${formatMoneyCents(c.fullCents)}`),
  ].join('\n');
}

// ---------------------------------------------------------------------------
// §4: Other charge constants
// ---------------------------------------------------------------------------

/** Preset options for the other-charges combobox (§4.6). */
export const OTHER_CHARGE_PRESETS = ['HOA Fees', 'Parking', 'Laundry', 'Animal Rent'] as const;

/** Maximum characters in an other-charge description (§4.3). */
export const OTHER_CHARGE_DESCRIPTION_MAX = 30;

// ---------------------------------------------------------------------------
// §6: Animal constants (all in cents per §C.8)
// ---------------------------------------------------------------------------

/** Default monthly rent per animal: $50. */
export const ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS = 5000;

/** Default security deposit per animal: $500 (meeting 2026-05-13 §2.1; was $300). */
export const ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS = 50000;

export const ANIMAL_COUNT_MIN = 1;
export const ANIMAL_COUNT_MAX = 9;

// ---------------------------------------------------------------------------
// AppFolio Pet shape + column mapping (the ONLY shape-dependent piece)
// ---------------------------------------------------------------------------

/**
 * A single animal as carried on an AppFolio applicant's `Pets[]`.
 *
 * PLACEHOLDER SHAPE. The AppFolio enrichment that populates these fields is
 * being built in parallel; the real key names are not yet known. Every field
 * is optional + loosely typed so a missing/odd value never throws — petToRow
 * coerces each to a display string. Field names appear in exactly ONE place:
 * petToRow (below). Adjust them there when the enrichment lands.
 */
export interface AppFolioPet {
  Name?: unknown;
  Type?: unknown;
  Weight?: unknown;
  Age?: unknown;
  /** Enrichment/intake: dog breed. */
  Breed?: unknown;
  /** Enrichment/intake: coat color. */
  Color?: unknown;
  /**
   * Intake (WEL-173): how `Age` is counted — 'months' for a young animal
   * ("4 mos"). Absent ⇒ years, which is every AppFolio age and every intake
   * age recorded before WEL-173, so those render exactly as before.
   */
  AgeUnit?: unknown;
  /** Intake: 'male' | 'female' (or any string). */
  Gender?: unknown;
  /** Intake: spay/neuter status (boolean or string). */
  SpayedNeutered?: unknown;
  /**
   * Enrichment/intake: animal license number. Rendered only when present.
   * AppFolio enrichment uses `License`; the intake mapper also writes `License`.
   * `LicenseNumber` is accepted as an alias for forward-compat.
   */
  License?: unknown;
  LicenseNumber?: unknown;
  /**
   * Intake: the noun that replaces the literal word "other" for an IntakeAnimal
   * of type 'other' (e.g. "rabbit"). When set it is the displayed type; the raw
   * "other" string is never shown.
   */
  OtherDescription?: unknown;
  /**
   * RRA per-animal charge exemption (docs/design/rra-workflow.md review fix
   * #4): the `rra_case_items` itemId of the APPROVED accommodation that
   * exempts THIS animal from animal rent + animal deposit. Written server-side
   * (intake merge); consumed only by chargeExemptAnimalCount/
   * chargeableAnimalCount. Opaque id — no accommodation/disability detail ever
   * rides this field. Absent everywhere today, so all existing rosters charge
   * exactly as before.
   */
  chargeExemptionCaseItemId?: string;
  /**
   * Build-stage RA-waiver note (WEL-104 revision): pre-send counterpart of
   * `chargeExemptionCaseItemId`. Set while building to zero the animal's rent +
   * deposit WITHOUT creating an RRA case; memorialized into a real case (and
   * replaced by a pointer) at lease-send. Counted by chargeExemptAnimalCount
   * identically.
   */
  raWaiveBuildTime?: boolean;
}

/** Coerce an unknown Pet field into a trimmed display string ('' when absent). */
function petCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

/** "male" -> "Male"; passes through blank. Title-cases a single-word value. */
function fmtTitle(v: unknown): string {
  const s = petCell(v);
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

/**
 * "Spayed/neutered" status → a short display phrase, or '' when unknown.
 * Accepts boolean (true/false), or a string ("true"/"yes"/"1" → spayed/neutered;
 * "false"/"no"/"0" → not). Anything else passes through trimmed.
 */
function fmtSpayedNeutered(v: unknown): string {
  if (v === true) return 'Spayed/neutered';
  if (v === false) return 'Not spayed/neutered';
  const s = petCell(v).toLowerCase();
  if (!s) return '';
  if (s === 'true' || s === 'yes' || s === '1') return 'Spayed/neutered';
  if (s === 'false' || s === 'no' || s === '0') return 'Not spayed/neutered';
  return petCell(v);
}

/**
 * The animal "type" for display: the OtherDescription noun (e.g. "rabbit") wins
 * over a raw Type of "other" so the lease never prints the literal word "other".
 * Otherwise the AppFolio Type passes through. Used by petToRow + dedup so the
 * displayed kind is consistent everywhere.
 */
function petDisplayType(pet: AppFolioPet): string {
  const rawType = petCell(pet.Type);
  const other = petCell(pet.OtherDescription);
  if (other && (!rawType || rawType.toLowerCase() === 'other')) return other;
  return rawType;
}

/**
 * Map one AppFolio Pet object (record.Applicant.Pets[]) to the Approved-Animals
 * display columns. AppFolio supplies Name/Type/Weight/Age; the pre-lease intake
 * enrichment additionally supplies breed/gender/spayedNeutered/license and the
 * 'other' description (rendered in place of the word "other"). This is the
 * SINGLE place AppFolio/intake Pet field names appear; downstream consumes the
 * returned row shape. The enriched fields are '' when not collected so the
 * AppFolio-only path renders exactly as before.
 */
export function petToRow(pet: AppFolioPet): {
  name: string;
  type: string;
  weight: string;
  age: string;
  breed: string;
  color: string;
  gender: string;
  spayedNeutered: string;
  license: string;
} {
  return {
    name: petCell(pet.Name),
    type: petDisplayType(pet),
    weight: fmtWeight(pet.Weight),
    age: fmtAge(pet.Age, pet.AgeUnit),
    breed: petCell(pet.Breed),
    color: petCell(pet.Color),
    gender: fmtTitle(pet.Gender),
    spayedNeutered: fmtSpayedNeutered(pet.SpayedNeutered),
    // AppFolio enrichment uses `License`; accept `LicenseNumber` as an alias.
    license: petCell(pet.License) || petCell(pet.LicenseNumber),
  };
}

/** "30.0" -> "30 lbs"; blank/non-numeric passes through unchanged. */
function fmtWeight(v: unknown): string {
  const s = petCell(v);
  if (!s) return '';
  const n = Number.parseFloat(s);
  return Number.isFinite(n) ? `${n} lbs` : s;
}

/** "1" -> "1 yr", "3" -> "3 yrs"; with an intake unit of 'months' (WEL-173)
 *  "1" -> "1 mo", "4" -> "4 mos". Blank/non-numeric passes through unchanged. */
function fmtAge(v: unknown, unit?: unknown): string {
  const s = petCell(v);
  if (!s) return '';
  const n = Number.parseFloat(s);
  if (!Number.isFinite(n)) return s;
  if (unit === 'months') return `${n} ${n === 1 ? 'mo' : 'mos'}`;
  return `${n} ${n === 1 ? 'yr' : 'yrs'}`;
}

// ---------------------------------------------------------------------------
// §0.5: LeaseFormState — the canonical form payload persisted in DynamoDB
// ---------------------------------------------------------------------------

export interface LeaseFormState {
  // --- Premises (editable — defaults from unit/property) ---
  premisesAddress: string;
  propertyName: string;
  unitLabel: string;

  // --- Signing (Phase 2 DocuSeal send) ---
  /**
   * Applicant id of the lead applicant — gets DocuSeal role `Resident1`
   * (order:0) and fills the inline resident fields. Optional; when unset the
   * submitter builder defaults to the first applicant in group order. The
   * default-selection logic lives in buildSubmitters, not here.
   */
  leadApplicantId?: string;

  // --- Term (§3) ---
  documentDate: string;           // yyyy-mm-dd
  leaseStartDate: string;         // yyyy-mm-dd
  leaseTermMonths: number;
  /** Persisted end date (§3.4). Default: computeEndDateFromTerm(leaseStartDate, leaseTermMonths). */
  leaseEndDate: string;           // yyyy-mm-dd
  /** Soft-warning acknowledgment for leases > 12 months (§3.6). Resets when leaseTermMonths transitions ≤ 12. */
  acknowledgedLongLease: boolean;

  // --- Early termination by notice (§4, staff-enabled negotiated option) ---
  /**
   * §4 negotiated no-fee early-termination-by-notice option. When true, §4
   * renders the "Early Termination by Notice" clause (the term stays the full
   * length; Resident gains a unilateral no-fee right to end early with 30 days'
   * notice, effective at a month-end no earlier than the configured floor). The
   * fee-based Early Termination Option is unaffected — the two coexist. Default false.
   */
  earlyTerminationNoticeEnabled: boolean;
  /**
   * Earliest permitted early-termination date (yyyy-mm-dd; a calendar month-end,
   * e.g. Feb 28). Required when earlyTerminationNoticeEnabled. Renders long-form
   * as {{ early_termination_earliest_date }}.
   */
  earlyTerminationNoticeEarliestDate: string;

  // --- Money (§C.8: all integer CENTS) ---
  /** Monthly rent in cents. buildDefaultFormState converts from AppFolio dollars. */
  monthlyRent: number;
  /** Base security deposit in cents. buildDefaultFormState converts from AppFolio dollars. */
  securityDeposit: number;
  /**
   * WEL-89: whether the unit has carpet. When true (the default), the CARPET
   * section — the LAST numbered section of the body — is included and the
   * carpet cleaning fee is collected. When false, the section is omitted
   * entirely and the carpet-fee field is suppressed. A unit fact: reset on a
   * move (PROPERTY_DERIVED_FIELDS) and carried across a renewal. Optional for
   * back-compat with drafts snapshotted before this field existed — both
   * renderers default an ABSENT value to "has carpet" (Python
   * `config.get('has_carpet', True)`; web `config.has_carpet !== false`), and
   * buildRenderConfig coalesces `s.hasCarpet !== false` so legacy drafts render
   * carpet exactly as before.
   */
  hasCarpet?: boolean;
  carpetCleaningFee: number;       // cents
  otherCharges: OtherCharge[];     // each row appears in the rent table; amounts are cents
  /** Staff-added nonrefundable one-time move-in fees (MOVE-IN FEES section). Absent = none. */
  oneTimeFees?: OneTimeFee[];

  // --- §6: Animal rent / deposit (replaces petDeposit) ---
  /**
   * Snapshot of the approved animals, taken from the AppFolio applicants' Pets
   * at draft-create time (buildDefaultFormState). Drives the now-static Approved
   * Animals table (one row per pet via petToRow) and the data-derived
   * animalCount. Optional for back-compat with drafts snapshotted before this
   * field existed — absent/empty renders no table rows and animalCount falls
   * back to 0 (or, on legacy drafts, the persisted animalCount).
   */
  pets?: AppFolioPet[];
  /**
   * Number of animals approved. 0 when animals === 'no'. Data-derived from
   * `pets.length` at default-build time; staff no longer enter it.
   *
   * Round-trip equality note: old schema `petDeposit: 300` (dollars) →
   * new schema `animalCount: 1, animalDepositPerAnimal: 30000` (cents).
   * Both produce the same $300 deposit contribution.
   */
  animalCount: number;
  /** Monthly rent per animal in cents. Default: ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS ($50) when hasAnimal. */
  animalRentPerAnimal: number;
  /** Security deposit per animal in cents. Default: ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS ($300) when hasAnimal. */
  animalDepositPerAnimal: number;
  /**
   * Number of free animals (waives both per-animal deposit + monthly animal rent contributions).
   * The manual staff waiver — see chargeableAnimalCount for the full chargeable
   * math (this count PLUS per-animal RRA exemptions net out of animalCount). Default 0.
   */
  freeAnimalCount: number;
  /**
   * RRA phase 2: set ONLY by the SERVER generate route when an active RRA
   * animal_charge hold matches the lease's subject — the builder UI never
   * writes it. While true chargeableAnimalCount is 0 ($0 animal rent +
   * deposit for the pendency of the accommodation request). Mirrored here so
   * the type stays in lockstep with the API copy and a server-stamped
   * formStateSnapshot round-trips; absent on every draft the UI builds.
   */
  animalChargesSuspended?: boolean;
  /**
   * RRA v2.2 part B (WEL-180, Miles 2026-10-30): the household animals an OPEN accommodation
   * request is about, marked by staff in the builder. Each is $0 now (out of the chargeable count)
   * and the lease prints the pending-decision clause naming them with the if-not-waived amounts
   * (`animal_decision_pending_paragraph`). Staff enter those amounts; they default to this draft's
   * per-animal rates. `key` is the pet's identity in this draft (`exemptionKey(name, type)` —
   * name lower-cased + '|' + dog/cat/other). Cleared by the server once no animal request is open
   * for the group, or for an animal an approval now exempts. Absent on every existing draft →
   * behavior unchanged.
   */
  animalsPendingDecision?: Array<{
    key: string;
    name: string;
    type?: 'dog' | 'cat' | 'other';
    ifDeniedRentCents: number;
    ifDeniedDepositCents: number;
  }>;
  // WEL-104: the `animalRaPending` / `animalRaCount` fields were REMOVED. They
  // only fed the builder's "reasonable accommodation request pending" checkbox,
  // whose lease paragraph the counsel revision already deleted (ANIM-03). RA
  // handling is internal policy per Rule H, never lease text, and is tracked by a
  // separate internal RRA workflow — not this form. (A pre-removal persisted key
  // in an old draft's JSON is harmless: no code reads it.)
  /**
   * Staff decisions on suspected-duplicate animals across co-applicants, keyed by
   * the pair's content key (see resolveGroupAnimals). 'same' merges the pair into
   * one roster row; 'different' keeps them separate and stops re-prompting. Pets/
   * animalCount are re-derived from the live group + these decisions on every
   * heal, so a resolution survives AppFolio re-syncs. Optional/back-compat:
   * absent → {} (no resolutions; auto-merge only, suspects shown separate).
   */
  petResolutions?: Record<string, PetResolution>;

  // --- Property structure ---
  propertyType: PropertyType;
  animals: AnimalChoice;
  parking: ParkingChoice;
  parkingSpots: number;
  parkingAssignedLabel: string;
  /**
   * WEL-61: optional SECOND assigned parking space number. Some units (e.g.
   * Towerview) have two assigned spaces. Optional/back-compat: absent or empty
   * → the lease renders exactly as before (only `parkingAssignedLabel`). When
   * non-empty, both labels render as a comma-joined list in §PARKING.
   */
  parkingAssignedLabel2?: string;
  parkingNotes: string;
  /** When true, parking is a charged line in §2 with amount `parkingChargeCents`. Default false. */
  parkingCharged: boolean;
  /** Parking monthly charge in cents (user enters dollars; stored as cents). Used only when `parkingCharged === true`. */
  parkingChargeCents?: number | null;
  yardCare: YardCare;
  /**
   * For resident-responsible yard care: 'resident_maintains' (default → renders
   * silent) vs 'lessor_arranges' (Lessor arranges + bills). Set from intake or
   * staff. Optional/back-compat: absent → 'resident_maintains'. */
  yardCareArrangement?: YardCareArrangement;
  /**
   * Resident designation (pre-lease intake wiring). Populated from the
   * household's designation intake at generate, or entered by staff in the
   * builder to satisfy the designation hard-block. Optional/back-compat: absent
   * on drafts snapshotted before this field existed — the RESIDENT DESIGNATION
   * section then renders the original blank DocuSeal fill fields. The generate
   * route HARD-BLOCKS (409) when neither this nor the group's intake carries a
   * designation; see assertDesignationProvided in leaseIntakeMerge.ts (API).
   */
  residentDesignation?: ResidentDesignation;
  hasOutdoorSpace: boolean;
  /**
   * HOA flag for the renderer (§8). Computed as `hoaSource !== 'none'`.
   * Stored redundantly so buildRenderConfig doesn't need to branch on hoaSource.
   */
  hasHoa: boolean;
  /**
   * HOA source (§8). Canonical field; `hasHoa` is derived from this.
   * Phase B HOA track wires the full reducer.
   */
  /**
   * Meeting 2026-05-14: dropped the 'one-off' variant — CC&Rs are managed
   * exclusively at the HOA level (HOA detail page). The lease builder only
   * overrides the default fee per-lease.
   */
  hoaSource: 'property-associated' | 'none';
  /**
   * Per-lease HOA fee override in cents. Empty/undefined = use the HOA's `defaultFeeCents`.
   * Explicit 0 or explicit null = "no HOA row in §2" (suppresses both default and any prior value).
   * Distinct from `hoaSource`: this controls only the §2 fee line, not whether HOA components render.
   */
  hoaFeeOverrideCents?: number | null;
  includeLbp: boolean;             // pre-1978 = true
  /**
   * Manual override for the AppFolio-derived `includeLbp` value (built-before-1978).
   * When `acknowledged === true`, the override is in effect and `includeLbp` reflects
   * `value`. When the user toggles the override but hasn't yet acknowledged, the
   * record exists with `acknowledged === false` (transient UI state). `null` (or
   * missing) = no override; use AF-derived value.
   */
  builtBefore1978Override?: { value: boolean; acknowledged: boolean } | null;
  /**
   * LBP Lessor's disclosure selections (Phase 2 DocuSeal send). Drive the
   * readonly checkbox prefill on the Broker submitter when `includeLbp`.
   * Optional; the submitter builder defaults both to the "none/no knowledge"
   * branch when unset.
   *   leadPresence: 'present' → leadPresence_present checked;
   *                 'none'    → leadPresence_none checked (default).
   *   leadRecords:  'provided' → leadRecords_provided checked;
   *                 'none'     → leadRecords_none checked (default).
   */
  leadPresence?: 'present' | 'none';
  leadRecords?: 'provided' | 'none';

  // --- §5: Utilities ---
  utilities: UtilityConfig[];

  // --- §9: Payment plan ---
  paymentPlanEnabled: boolean;
  paymentPlan: PaymentPlanConfig | null;

  // --- Other ---
  otherTerms: string;
  /** When true, the OTHER TERMS section is prefaced with the precedence
   *  sentence. Ignored when otherTerms is blank (the section is omitted). */
  otherTermsConflict?: boolean;
  fireSprinkler: boolean;
  fireAlarm: boolean;
  noSmokingProperty: boolean;
  emergencyNotificationPlan: boolean;
  emergencyRelocationPlan: boolean;
  emergencyEvacuationPlan: boolean;
  /**
   * Per-lease override for the AF Detectors custom-field disclosure (MFR
   * only). When null/undefined, use `g.unit?.detectors`. Useful when AF data
   * is absent or wrong (~399/471 units have unset Has Detector? today).
   * SFR ignores this field — the §9 detector disclosure is MFR-gated.
   */
  detectorTypeOverride?: 'hardwired' | 'battery' | 'mix' | null;
}

// ---------------------------------------------------------------------------
// ApplicationGroupShape — AppFolio wire shape (stays in DOLLARS; §B Decision 15)
// ---------------------------------------------------------------------------

export interface ApplicationGroupShape {
  groupId: string;
  status: string;
  desiredMoveInDate: string | null;
  /** Dollar amount (not cents) — AppFolio wire format. */
  listedRent: number | null;
  /** Dollar amount (not cents) — AppFolio wire format. */
  deposit: number | null;
  hasAnimal: boolean;
  /**
   * Part 2: application-entered minors / occupants (AppFolio Dependents). DOBs
   * drive the minor coming-of-age to-do (Part 4). Optional for back-compat with
   * snapshots taken before this field existed.
   */
  minors?: Array<{
    key: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string | null;
    relationshipToHead: string | null;
  }>;
  /** Part 1/2: tenancy move-out signal for a converted group (or null). */
  tenancyMoveOut?: {
    movedOut: boolean;
    moveOutOn: string | null;
    status: string | null;
  } | null;
  /** True if any application in the group was converted into a tenancy. */
  hasTenant?: boolean;
  applicants: Array<{
    id: string;
    firstName: string;
    lastName: string;
    isPrimary: boolean;
    /** AppFolio PrimaryEmail.EmailAddress; null when AppFolio has no email on file. */
    email: string | null;
    /** AppFolio PrimaryPhoneNumber.Number; null when absent. */
    phone: string | null;
    /** AppFolio DateOfBirth (yyyy-mm-dd); null when AppFolio has no DOB on file.
     *  Drives the adults-only DocuSeal submitter filter (Phase 2 refinement). */
    dateOfBirth: string | null;
  }>;
  property?: {
    id: string;
    name: string | null;
    address: string | null;
    yearBuilt: number | null;
    propertyType: string | null;
    catsAllowed: boolean | null;
    dogsAllowed: boolean | null;
    /** §10.5: routes the contacts table's Electricity row when tenant_direct. */
    electricalProvider?: 'Pacific Power' | 'Columbia REA' | null;
    /** §5.7: total units at this property (for ratio sanity warning). */
    unitCount?: number | null;
    /** meeting-2026-05-13 §9.2: garbage hauler / city name, verbatim from AF. */
    garbageProvider?: string | null;
    /** meeting-2026-05-13 §5.4: soft default for the Yard Care utility row. */
    yardCareAlwaysProvided?: boolean;
    /**
     * Staff-set per-property "has yard" override (consumer_whp.property_settings.
     * has_yard). When explicitly `true`/`false` it WINS over the proxy for the
     * lease yard-care default; `null`/undefined = unset → use the proxy (today's
     * behavior). buildDefaultFormState runs SERVER-SIDE, where this field is
     * stamped onto the group (gated behind PROPERTY_SETTINGS_READ); the web never
     * sets it, so the field is undefined in client-built state and the proxy stands.
     */
    hasYard?: boolean | null;
  };
  unit?: {
    id: string;
    name: string | null;
    address: string | null;
    squareFeet: number | null;
    bedrooms: number | null;
    bathrooms: number | null;
    /** Dollar amount (not cents) — AppFolio wire format. */
    listedRent: number | null;
    /** Dollar amount (not cents) — AppFolio wire format. */
    deposit: number | null;
    utilitiesIncluded: string[];
    /** §10.5: gates whether Gas can be tenant_direct. */
    hasGas?: boolean | null;
    /** meeting-2026-05-13 §6.2: smoke/CO detector power source, verbatim AF OptionValue. */
    detectors?: string | null;
    /** meeting-2026-05-13 §9.2: soft default for the Washer Dryer charge row. */
    washerDryerProvided?: boolean;
  };
}

// ---------------------------------------------------------------------------
// buildDefaultFormState — single dollars→cents conversion boundary
// ---------------------------------------------------------------------------

function inferPropertyType(g: ApplicationGroupShape): PropertyType {
  const t = (g.property?.propertyType ?? '').toLowerCase();
  if (t.includes('single')) return 'sfr';
  if (t.includes('multi') || t.includes('apartment') || t.includes('condo')) return 'mfr';
  return 'mfr';
}

// ---------------------------------------------------------------------------
// §6.1 Approved-animals dedup across co-applicants
//
// Co-applicants in one household each list the household's pets on their own
// application, so the same animal arrives 2–4 times — often with minor typos
// (case, spacing, "Penny"/"Pennie", "50" vs "50.0", a "Dog, " breed prefix).
// We collapse those without ever dropping a genuinely distinct animal:
//
//   - SAME (auto-merge, silent): identical name once normalized (case / spacing
//     / punctuation) AND a compatible kind. Pure formatting noise.
//   - SUSPECT (surfaced in the UI for staff to confirm): a name typo, the same
//     name on a different kind, or different names but matching breed+weight+age.
//   - DIFFERENT (separate line item): anything else — genuinely unique animals.
//
// HARD RULE (staff): two pets from the SAME applicant are NEVER merged or
// flagged — one person doesn't list the same animal twice, so two entries from
// one applicant are two real animals. Same-applicant pairs short-circuit to
// 'different'. Staff decisions on suspects persist in
// LeaseFormState.petResolutions keyed by the two pets' content, so they survive
// the AppFolio re-sync / draft heal.
// ---------------------------------------------------------------------------

/** A pet plus the applicant who listed it (drives the cross-applicant rule + UI). */
interface SourcedPet {
  pet: AppFolioPet;
  applicantId: string;
  applicantLabel: string;
  order: number; // applicant position; 0 = lead/primary (wins representative ties)
  key: string; // stable content key
}

/** Staff resolution of a suspected-duplicate pair. */
export type PetResolution = 'same' | 'different';

/** An unresolved possible-duplicate surfaced to staff in the Animals section. */
export interface AnimalSuspect {
  /** Stable key for the pair; staff's choice is stored at petResolutions[key]. */
  key: string;
  a: { name: string; type: string; weight: string; age: string; applicant: string };
  b: { name: string; type: string; weight: string; age: string; applicant: string };
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 0; i < a.length; i++) {
    const cur = [i + 1];
    for (let j = 0; j < b.length; j++) {
      cur.push(Math.min(prev[j + 1]! + 1, cur[j]! + 1, prev[j]! + (a[i] === b[j] ? 0 : 1)));
    }
    prev = cur;
  }
  return prev[b.length]!;
}

/** Name → lowercase, punctuation/space-collapsed ("Penny!" and "penny " → "penny"). */
function normalizePetName(v: unknown): string {
  return petCell(v).toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** Type → lowercase, drop a leading species prefix ("Dog, Husky" → "husky"). */
function normalizePetType(v: unknown): string {
  return petCell(v)
    .toLowerCase()
    .replace(/^(dog|cat|bird|reptile|fish|rabbit|other)\s*[,:/-]\s*/, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function petNumOrNull(v: unknown): number | null {
  const n = Number.parseFloat(petCell(v));
  return Number.isFinite(n) ? n : null;
}

function petCompleteness(p: AppFolioPet): number {
  return (
    (petCell(p.Name) ? 1 : 0) +
    (petCell(p.Type) ? 1 : 0) +
    (petCell(p.Weight) ? 1 : 0) +
    (petCell(p.Age) ? 1 : 0)
  );
}

function petContentKey(p: AppFolioPet): string {
  return [
    normalizePetName(p.Name),
    normalizePetType(p.Type),
    petNumOrNull(p.Weight) ?? '',
    petNumOrNull(p.Age) ?? '',
  ].join('|');
}

/**
 * Classify a pet pair into one of the three dedup tiers. Pairs from the SAME
 * applicant are forced to 'different' (the hard rule above). Exported for tests.
 */
export function classifyPetPair(a: SourcedPet, b: SourcedPet): 'same' | 'suspect' | 'different' {
  if (a.applicantId === b.applicantId) return 'different';

  const na = normalizePetName(a.pet.Name);
  const nb = normalizePetName(b.pet.Name);
  const ta = normalizePetType(a.pet.Type);
  const tb = normalizePetType(b.pet.Type);
  const wa = petNumOrNull(a.pet.Weight);
  const wb = petNumOrNull(b.pet.Weight);
  const ga = petNumOrNull(a.pet.Age);
  const gb = petNumOrNull(b.pet.Age);

  const typeBoth = !!ta && !!tb;
  const typeMatch =
    typeBoth &&
    (ta === tb ||
      ta.includes(tb) ||
      tb.includes(ta) ||
      (Math.min(ta.length, tb.length) >= 4 && levenshtein(ta, tb) <= 2));
  const typeConflict = typeBoth && !typeMatch;
  const weightClose =
    wa !== null && wb !== null && Math.abs(wa - wb) <= Math.max(3, 0.15 * Math.max(wa, wb));
  const weightConflict = wa !== null && wb !== null && !weightClose;
  const ageClose = ga !== null && gb !== null && Math.abs(ga - gb) <= 1;
  const ageConflict = ga !== null && gb !== null && !ageClose;

  const attrCorroborate = typeMatch || weightClose || ageClose;
  const attrConflict = typeConflict || weightConflict || ageConflict;
  const attrStrong = typeMatch && weightClose && ageClose;

  if (na && nb) {
    if (na === nb) return typeConflict ? 'suspect' : 'same'; // same name, different species → suspect
    const minLen = Math.min(na.length, nb.length);
    const cap = minLen <= 3 ? 0 : minLen <= 6 ? 2 : 3;
    if (cap > 0 && levenshtein(na, nb) <= cap) {
      // a name typo: suspect unless the attributes actively contradict it
      return attrConflict && !attrCorroborate ? 'different' : 'suspect';
    }
  }
  // different/absent names: only a strong full-attribute match is suspect
  return attrStrong ? 'suspect' : 'different';
}

function describeSourcedPet(s: SourcedPet): AnimalSuspect['a'] {
  const r = petToRow(s.pet);
  return { name: r.name, type: r.type, weight: r.weight, age: r.age, applicant: s.applicantLabel };
}

/** Gather every applicant's pets, tagged with their source applicant. */
function collectSourcedPets(g: ApplicationGroupShape): SourcedPet[] {
  const applicants = (g as { applicants?: unknown }).applicants;
  if (!Array.isArray(applicants)) return [];
  const out: SourcedPet[] = [];
  applicants.forEach((a, order) => {
    const ap = a as { id?: unknown; firstName?: unknown; pets?: unknown; Pets?: unknown };
    const raw = ap.pets ?? ap.Pets;
    if (!Array.isArray(raw)) return;
    const applicantId = ap.id != null ? String(ap.id) : `idx-${order}`;
    const applicantLabel = petCell(ap.firstName) || `Applicant ${order + 1}`;
    for (const pet of raw as AppFolioPet[]) {
      out.push({ pet, applicantId, applicantLabel, order, key: petContentKey(pet) });
    }
  });
  return out;
}

/**
 * Resolve a group's approved animals: collapse cross-applicant duplicates
 * (auto-merge + staff-confirmed) and surface the suspect pairs staff still need
 * to confirm. `resolutions` is LeaseFormState.petResolutions. Pure.
 */
export function resolveGroupAnimals(
  g: ApplicationGroupShape,
  resolutions?: Record<string, PetResolution>,
): { roster: AppFolioPet[]; suspects: AnimalSuspect[] } {
  const sourced = collectSourcedPets(g);
  const res = resolutions ?? {};
  const n = sourced.length;

  const parent = sourced.map((_, i) => i);
  const find = (i: number): number => {
    let r = i;
    while (parent[r] !== r) r = parent[r] = parent[parent[r]!]!;
    return r;
  };
  const union = (i: number, j: number) => {
    parent[find(i)] = find(j);
  };

  const rawSuspects: Array<{ i: number; j: number; key: string }> = [];
  for (let i = 0; i < n; i++) {
    for (let j = i + 1; j < n; j++) {
      const tier = classifyPetPair(sourced[i]!, sourced[j]!);
      if (tier === 'same') {
        union(i, j);
        continue;
      }
      if (tier !== 'suspect') continue;
      const key = [sourced[i]!.key, sourced[j]!.key].sort().join('::');
      const decision = res[key];
      if (decision === 'same') union(i, j);
      else if (decision === 'different') continue;
      else rawSuspects.push({ i, j, key });
    }
  }

  // One representative per cluster: richest record, lead applicant wins ties.
  const clusters = new Map<number, number[]>();
  for (let i = 0; i < n; i++) {
    const r = find(i);
    if (!clusters.has(r)) clusters.set(r, []);
    clusters.get(r)!.push(i);
  }
  const reps: Array<{ pet: AppFolioPet; firstIndex: number }> = [];
  for (const members of clusters.values()) {
    const best = [...members].sort(
      (x, y) =>
        petCompleteness(sourced[y]!.pet) - petCompleteness(sourced[x]!.pet) ||
        sourced[x]!.order - sourced[y]!.order,
    )[0]!;
    reps.push({ pet: sourced[best]!.pet, firstIndex: Math.min(...members) });
  }
  reps.sort((a, b) => a.firstIndex - b.firstIndex);
  const roster = reps.map((r) => r.pet);

  const seen = new Set<string>();
  const suspects: AnimalSuspect[] = [];
  for (const { i, j, key } of rawSuspects) {
    if (find(i) === find(j)) continue; // unioned elsewhere (transitive auto-merge)
    if (seen.has(key)) continue;
    seen.add(key);
    suspects.push({ key, a: describeSourcedPet(sourced[i]!), b: describeSourcedPet(sourced[j]!) });
  }
  return { roster, suspects };
}

/**
 * Back-compat flat roster (auto-merge + the supplied resolutions; suspect info
 * dropped). buildDefaultFormState / withFreshAnimalSnapshot use this for the
 * lease's animal rows; the UI uses resolveGroupAnimals to also get the suspects.
 */
function collectPetsFromGroup(
  g: ApplicationGroupShape,
  resolutions?: Record<string, PetResolution>,
): AppFolioPet[] {
  return resolveGroupAnimals(g, resolutions).roster;
}

/**
 * WEL-61: combine the primary assigned-parking label with an optional second
 * space number into the single `assigned_parking` render variable. Kept
 * byte-identical to the bare primary label when there is no (non-empty) second
 * space, so one-space units and pre-WEL-61 drafts render exactly as before.
 * When a second space is present, the two are joined as "12, 14".
 *
 * Mirrors `combineAssignedParking` in apps/api/src/lib/leaseConfig.ts — keep
 * the two in sync (see "Dual lease renderers" in CLAUDE.md).
 */
export function combineAssignedParking(label1: string, label2?: string): string {
  const second = (label2 ?? '').trim();
  if (!second) return label1;
  const first = label1.trim();
  // Defensive: if the primary somehow is blank, don't emit a dangling ", 14".
  if (!first) return second;
  // Don't repeat an identical space number (e.g. "12" + "12" → "12"). (Codex review.)
  if (first.toLowerCase() === second.toLowerCase()) return label1;
  return `${first}, ${second}`;
}

export function buildDefaultFormState(g: ApplicationGroupShape): LeaseFormState {
  const today = new Date();
  const startDate = g.desiredMoveInDate
    ? new Date(g.desiredMoveInDate)
    : new Date(today.getFullYear(), today.getMonth() + 1, 1);

  const propertyType = inferPropertyType(g);
  const yearBuilt = g.property?.yearBuilt ?? null;
  const includeLbp = yearBuilt !== null && yearBuilt < 1978;

  // Utility defaults from AppFolio's UtilitiesIncluded list.
  // §10.5: in WHP's market, water and sewer are NEVER tenant-direct. Default
  // them to `landlord_provided` when the property doesn't explicitly include
  // them, instead of the generic tenant_direct default.
  const included = new Set((g.unit?.utilitiesIncluded ?? []).map((s) => s.toLowerCase()));
  const utilityState = (name: string): UtilityState => {
    const lower = name.toLowerCase();
    if (lower === 'water' || lower === 'sewer') {
      return included.has(lower) ? 'landlord_billed_full' : 'landlord_provided';
    }
    return included.has(lower) ? 'landlord_billed_full' : 'tenant_direct';
  };

  // Address: prefer unit, fall back to property.
  const premisesAddress = g.unit?.address || g.property?.address || '';

  const leaseStartDateStr = ymd(startDate);
  const leaseTermMonths = 12;

  // Dollars → cents conversion (§B Decision 15: AppFolio stays in dollars;
  // buildDefaultFormState is the single conversion boundary). Routed through
  // dollarsNumberToCents() to keep the cents-conversion semantics consistent
  // with parseMoneyDollarsToCents (both end in Math.round(× 100); both
  // defensively coerce non-finite to 0).
  const monthlyRentCents = dollarsNumberToCents(g.unit?.listedRent ?? g.listedRent ?? 0);
  // Product (confirmed): when AppFolio carries no deposit, the security deposit
  // defaults to the listed rent. This is the agreed default; staff override it
  // in the builder.
  const securityDepositCents = dollarsNumberToCents(
    g.unit?.deposit ?? g.deposit ?? g.unit?.listedRent ?? g.listedRent ?? 0,
  );

  // §6: animals are now data-driven. Snapshot the applicants' Pets and derive
  // the count from the data — staff no longer enter a count. `hasAnimal` from
  // AppFolio still seeds the permitted toggle, but the count/rows come from the
  // pets list. When pets are present, hasAnimalDerived is true regardless of the
  // (possibly stale) g.hasAnimal flag.
  const pets = collectPetsFromGroup(g);
  const animalCount = pets.length;
  const hasAnimalDerived = animalCount > 0 || g.hasAnimal;

  return {
    premisesAddress,
    propertyName: g.property?.name ?? '',
    unitLabel: g.unit?.name ?? '',
    documentDate: ymd(today),
    leaseStartDate: leaseStartDateStr,
    leaseTermMonths,
    leaseEndDate: computeEndDateFromTerm(leaseStartDateStr, leaseTermMonths),
    acknowledgedLongLease: false,
    // §4 early-termination-by-notice option: off unless staff negotiate it.
    earlyTerminationNoticeEnabled: false,
    earlyTerminationNoticeEarliestDate: '',
    monthlyRent: monthlyRentCents,
    securityDeposit: securityDepositCents,
    // WEL-89: carpet is on by default (most units have carpet). Staff flip it off
    // for hard-surface units; doing so drops the (last) CARPET section + the fee.
    hasCarpet: true,
    carpetCleaningFee: 0,
    otherCharges: [],
    // §6: animal rent/deposit replaces petDeposit. The animals are now
    // data-driven: `pets` is the snapshot, `animalCount` is derived from it
    // (pets.length, not a hardcoded 1), and `animals` is permitted whenever the
    // data says there's at least one animal. The charge fields (per-animal
    // rent/deposit + free-count) stay staff-editable and seed to the defaults
    // when animals are permitted.
    pets,
    animalCount,
    animalRentPerAnimal: hasAnimalDerived ? ANIMAL_RENT_PER_ANIMAL_DEFAULT_CENTS : 0,
    animalDepositPerAnimal: hasAnimalDerived ? ANIMAL_DEPOSIT_PER_ANIMAL_DEFAULT_CENTS : 0,
    freeAnimalCount: 0,
    petResolutions: {},
    propertyType,
    animals: hasAnimalDerived ? 'yes' : 'no',
    parking: propertyType === 'mfr' ? 'assigned' : 'unassigned',
    parkingSpots: 1,
    parkingAssignedLabel: '',
    parkingNotes: '',
    parkingCharged: false,
    // Yard care default. The staff-set per-property override
    // (consumer_whp.property_settings.has_yard, surfaced as g.property.hasYard by
    // the server when PROPERTY_SETTINGS_READ is on) WINS over the proxy: `true` →
    // resident-responsible, `false` → landlord. When unset (undefined/null —
    // always the case for web-built state, which never carries it) we fall back to
    // the existing proxy: provided/included config, else the property-type
    // heuristic (SFR ⇒ resident-maintained, else landlord). Staff still override
    // this in the builder. (buildDefaultFormState runs server-side for real
    // drafts; this branch matters only for the override-stamped server path.)
    yardCare:
      g.property?.hasYard === true
        ? 'resident'
        : g.property?.hasYard === false
          ? 'landlord'
          : g.property?.yardCareAlwaysProvided
            ? 'landlord'
            : propertyType === 'sfr'
              ? 'resident'
              : 'landlord',
    hasOutdoorSpace: false,
    hasHoa: false,
    hoaSource: 'none',
    includeLbp,
    utilities: [
      { name: 'Water', state: utilityState('Water') },
      { name: 'Sewer', state: utilityState('Sewer') },
      { name: 'Garbage', state: utilityState('Garbage') },
      { name: 'Electricity', state: utilityState('Electricity') },
      { name: 'Gas', state: utilityState('Gas') },
    ],
    paymentPlanEnabled: false,
    paymentPlan: null,
    otherTerms: '',
    fireSprinkler: false,
    fireAlarm: false,
    noSmokingProperty: true,
    emergencyNotificationPlan: false,
    emergencyRelocationPlan: false,
    emergencyEvacuationPlan: false,
  };
}

/**
 * Re-derive the data-driven animal snapshot from the live application group.
 *
 * `pets` and `animalCount` mirror the application's Pets — staff never edit them
 * — so refreshing them whenever we have the live group keeps a draft in sync
 * with the latest AppFolio enrichment and heals drafts snapshotted before the
 * enrichment landed (the cause of an existing draft showing "0 animals on file"
 * while the application clearly has pets). Staff-editable fields are preserved;
 * `freeAnimalCount` is only re-clamped into [0, animalCount] so a prior waiver
 * can't exceed the (possibly smaller) refreshed count or go NaN. Pure: returns a
 * new state, never mutates `s`.
 */
export function withFreshAnimalSnapshot(
  s: LeaseFormState,
  g: ApplicationGroupShape,
): LeaseFormState {
  const pets = collectPetsFromGroup(g, s.petResolutions);
  const animalCount = pets.length;
  const prevFree = Number.isFinite(s.freeAnimalCount) ? s.freeAnimalCount : 0;
  const freeAnimalCount = Math.min(Math.max(0, prevFree), animalCount);
  return { ...s, pets, animalCount, freeAnimalCount };
}

// ---------------------------------------------------------------------------
// Chargeable-animal math (RRA phase 2 — the ONE place the count is computed)
// ---------------------------------------------------------------------------

// SYNC WITH apps/api/src/lib/leaseConfig.ts (same helpers; the server copy is
// authoritative for the generated PDF, this copy drives preview + builder UI).

/** The slice of LeaseFormState the chargeable-animal math reads. */
export type AnimalChargeStateSlice = Pick<
  LeaseFormState,
  'pets' | 'animalCount' | 'freeAnimalCount' | 'animalChargesSuspended' | 'animalsPendingDecision'
>;

/** Finite-number coercion (same defensive posture as buildRenderConfig's
 *  safeCents): legacy drafts can carry undefined for post-schema-bump fields. */
function finiteCount(n: unknown): number {
  return typeof n === 'number' && Number.isFinite(n) ? n : 0;
}

/**
 * How many roster animals carry an RRA charge exemption — either a memorialized
 * per-animal pointer (AppFolioPet.chargeExemptionCaseItemId) OR a pre-send
 * build-time waiver note (AppFolioPet.raWaiveBuildTime, WEL-104 revision). Both
 * zero the same animal, so an animal briefly carrying both still counts once.
 * Per-animal by design (review fix #4): tied to the specific animal, so a
 * replacement pet on a renewal roster is never silently exempted. Mirrors the
 * server chargeExemptAnimalCount exactly. 0 on every existing lease.
 */
export function chargeExemptAnimalCount(s: AnimalChargeStateSlice): number {
  const pets = Array.isArray(s.pets) ? s.pets : [];
  return pets.filter(isChargeExemptPet).length;
}

/** A roster pet that carries an RRA charge exemption: a memorialized per-animal
 *  pointer OR a build-time waiver note. The one predicate behind
 *  chargeExemptAnimalCount and the pending-decision matcher below. */
function isChargeExemptPet(p: AppFolioPet | undefined): boolean {
  return (
    (typeof p?.chargeExemptionCaseItemId === 'string'
      && p.chargeExemptionCaseItemId.length > 0)
    || p?.raWaiveBuildTime === true
  );
}

// RRA v2.2 part B (WEL-180, Miles 2026-10-30) — animals an OPEN accommodation
// request is about. Staff mark them in the builder (`animalsPendingDecision`);
// each is out of the chargeable count (so $0 rent + $0 deposit NOW) and the lease
// prints the pending-decision clause (`animal_decision_pending_paragraph`) with
// the if-not-waived amounts. Absent on every existing draft → nothing changes.

/** One `animalsPendingDecision` entry. */
export type PendingDecisionAnimal = NonNullable<LeaseFormState['animalsPendingDecision']>[number];

/** dog / cat / other for a roster pet — the same classification as the server's
 *  coerceAnimalType (leaseIntakeForm.ts) on the displayed type, so an entry's key
 *  (`exemptionKey(name, type)`) and a pet agree. */
function petDecisionKind(pet: AppFolioPet): 'dog' | 'cat' | 'other' {
  const t = petDisplayType(pet).toLowerCase();
  if (/\bdog\b|puppy|canine/.test(t)) return 'dog';
  if (/\bcat\b|kitten|feline/.test(t)) return 'cat';
  return 'other';
}

/**
 * The pending-decision entries that match an animal actually on the roster, each
 * paired with the pet it claims (1:1, in entry order). An entry claims the first
 * unclaimed pet whose name (case-insensitive) equals the key's name part and whose
 * kind equals the key's type part (an empty type part matches any kind). A pet that
 * already carries a charge exemption is never claimed — an approved animal is $0 for
 * good and is never "pending" — and an entry that matches no pet contributes nothing
 * (the clause never names an animal the lease does not list). [] when the field is
 * absent, so every existing lease is untouched.
 */
export function matchPendingDecisionAnimals(
  s: Pick<LeaseFormState, 'pets' | 'animalsPendingDecision'>,
): Array<{ entry: PendingDecisionAnimal; pet: AppFolioPet }> {
  const list = Array.isArray(s.animalsPendingDecision) ? s.animalsPendingDecision : [];
  if (list.length === 0) return [];
  const pets = Array.isArray(s.pets) ? s.pets : [];
  const claimed = new Set<number>();
  const out: Array<{ entry: PendingDecisionAnimal; pet: AppFolioPet }> = [];
  for (const entry of list) {
    const key = typeof entry?.key === 'string' ? entry.key.trim().toLowerCase() : '';
    if (!key) continue;
    const cut = key.lastIndexOf('|');
    const wantName = (cut < 0 ? key : key.slice(0, cut)).trim();
    const wantType = cut < 0 ? '' : key.slice(cut + 1).trim();
    const idx = pets.findIndex(
      (p, i) =>
        !claimed.has(i)
        && !isChargeExemptPet(p)
        && petCell(p?.Name).trim().toLowerCase() === wantName
        && (wantType === '' || petDecisionKind(p) === wantType),
    );
    if (idx < 0) continue;
    claimed.add(idx);
    out.push({ entry, pet: pets[idx]! });
  }
  return out;
}

/**
 * THE chargeable animal count — the single source of truth for every animal
 * rent/deposit computation (preview render, builder totals, payment-plan
 * ceiling; the API copy mirrors it for the generated PDF):
 *
 *   0                                     while animalChargesSuspended
 *                                         (active RRA animal_charge hold —
 *                                         pending-request freeze), else
 *   max(0, animalCount − freeAnimalCount − exempt animals − animals pending
 *   an open request's decision)
 *
 * `freeAnimalCount` keeps its existing semantics untouched (manual staff
 * waiver); the RRA per-animal exemption layers on top additively. With no
 * suspension, no exemption and no free count this is exactly the historical
 * max(0, animalCount) — byte-identical output for every existing lease.
 */
export function chargeableAnimalCount(s: AnimalChargeStateSlice): number {
  if (s.animalChargesSuspended === true) return 0;
  return Math.max(
    0,
    finiteCount(s.animalCount)
      - finiteCount(s.freeAnimalCount)
      - chargeExemptAnimalCount(s)
      - matchPendingDecisionAnimals(s).length,
  );
}

// ---------------------------------------------------------------------------
// Private render helpers
// ---------------------------------------------------------------------------

function fmtDateLong(ymdStr: string): string {
  const d = new Date(ymdStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmtDateShort(ymdStr: string): string {
  const d = new Date(ymdStr + 'T00:00:00');
  return d.toLocaleDateString('en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

function yesNoPhrase(b: boolean): string {
  return b ? 'does' : 'does not';
}

// meeting-2026-05-13 §6.2: derive the human-readable detector power-source
// phrase from the raw AppFolio OptionValue string. Pattern-matches three
// keywords; falls back to the verbatim OptionValue when no pattern matches
// (so a future OptionValue not anticipated here still surfaces in the lease
// rather than disappearing silently).
function detectorPowerSourcePhrase(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (/hardwir/i.test(trimmed)) return 'hardwired';
  if (/mix/i.test(trimmed)) return 'a mix of battery and hardwired';
  if (/battery/i.test(trimmed)) return 'battery-operated';
  return trimmed; // verbatim fallback for unanticipated OptionValues
}

/**
 * Resolve the effective detector value for disclosure: per-lease override
 * (LeaseFormState.detectorTypeOverride) wins over the AF Detectors custom
 * field when set; otherwise falls back to the unit's AF value. Returns
 * null when both are absent.
 */
function effectiveDetectorValue(s: LeaseFormState, g: ApplicationGroupShape): string | null {
  if (s.detectorTypeOverride) return s.detectorTypeOverride;
  return g.unit?.detectors ?? null;
}

/**
 * meeting-2026-05-13 §6.2: detector power-source disclosure sentence for §9
 * (Smoke/CO). MFR-only — SFR returns '' so the placeholder collapses to a
 * blank line. Returns '' when the AF Detectors value is unset and no
 * per-lease override is set.
 */
export function buildDetectorPowerSourceDisclosure(
  g: ApplicationGroupShape,
  s: LeaseFormState,
): string {
  if (s.propertyType !== 'mfr') return '';
  const phrase = detectorPowerSourcePhrase(effectiveDetectorValue(s, g));
  if (!phrase) return '';
  return `Detectors at this property are ${phrase}.`;
}

// Per RCW 59.18.060(12): MFR landlords must provide a written fire-safety
// notice at lease signing; SFR doesn't need this disclosure (the six items
// — sprinkler / fire alarm / no-smoking policy / emergency plans — are
// multifamily concerns). Returns '' for SFR so core_lease.md collapses
// out the whole section.
function buildFireSafetyNoticeSection(s: LeaseFormState, g?: ApplicationGroupShape): string {
  if (s.propertyType !== 'mfr') return '';
  const items: Array<{ label: string; present: boolean }> = [
    { label: 'A fire sprinkler system', present: s.fireSprinkler },
    { label: 'A fire alarm system', present: s.fireAlarm },
    { label: 'A NO smoking policy for ENTIRE property', present: s.noSmokingProperty },
    { label: 'An emergency notification plan', present: s.emergencyNotificationPlan },
    { label: 'An emergency relocation plan', present: s.emergencyRelocationPlan },
    { label: 'An emergency evacuation plan', present: s.emergencyEvacuationPlan },
  ];
  const has = items.filter((i) => i.present);
  const doesNot = items.filter((i) => !i.present);
  // meeting-2026-05-13 §6.2: detector power-source bullet appears in the
  // signed Fire Safety Notice block alongside has / does-not-have items.
  // Per-lease override wins over AF; otherwise falls back to unit data.
  const detectorPhrase = detectorPowerSourcePhrase(
    g ? effectiveDetectorValue(s, g) : null,
  );
  const lines: string[] = ['## FIRE AND SAFETY', ''];
  if (has.length > 0) {
    lines.push('This property has:', '');
    for (const item of has) lines.push(`- ${item.label}`);
    lines.push('');
  }
  if (doesNot.length > 0) {
    lines.push('This property does not have:', '');
    for (const item of doesNot) lines.push(`- ${item.label}`);
    lines.push('');
  }
  if (detectorPhrase) {
    lines.push(`- Detectors at this property are ${detectorPhrase}.`, '');
  }
  lines.push(
    'Resident acknowledges receipt of this notice regarding fire safety and protection information for the Premises.',
    '',
    '[[RESIDENT_INITIALS]]',
  );
  return lines.join('\n');
}

// meeting-2026-05-13 §5.6: the three new pre-rendered subsections under
// the renamed §UTILITIES heading. Each emits its `### heading` plus body,
// or an empty string when the subsection does not apply.

/**
 * Water+Sewer travel together in WHP's market: the configure UI exposes one
 * combined "Water & Sewer" control. Persisted state can drift if the lease
 * was edited before the meeting-2026-05-13 combined-control shipped, or if
 * an autosave landed mid-toggle. Normalize at every render boundary so the
 * lease body always shows Sewer mirroring Water (state + ratio).
 */
export function normalizeUtilities(
  utilities: UtilityConfig[],
  options?: { hasGas?: boolean },
): UtilityConfig[] {
  const water = utilities.find((u) => u.name === 'Water');
  const hasGas = options?.hasGas === true;
  return utilities
    // Meeting 2026-05-13 §5.3: filter Gas unless the property's AF
    // "Has Gas?" custom field is explicitly true. Treat unknown/null/false
    // the same — "no data that gas is present → don't render Gas."
    .filter((u) => u.name !== 'Gas' || hasGas)
    .map((u) => {
      if (u.name !== 'Sewer' || !water) return u;
      const synced: UtilityConfig = { name: u.name, state: water.state };
      if (water.shareNumerator !== undefined) synced.shareNumerator = water.shareNumerator;
      if (water.shareDenominator !== undefined) synced.shareDenominator = water.shareDenominator;
      return synced;
    });
}

/**
 * Build the OTHER TERMS section markdown (substituted into core_lease.md via
 * `{{ other_terms_section }}`).
 *  - Blank otherTerms → empty string, so the section (and its number) is omitted
 *    entirely and the following sections renumber down.
 *  - Non-blank → the `## OTHER TERMS` heading + the staff-entered terms verbatim,
 *    with NO preamble by default. When `otherTermsConflict` is checked, the
 *    precedence sentence is prefaced.
 */
export function buildOtherTermsSection(s: LeaseFormState): string {
  const body = (s.otherTerms ?? '').trim();
  if (!body) return '';
  const preamble = s.otherTermsConflict
    ? 'Any terms listed in this section will take precedence if there is any conflict in this document.\n\n'
    : '';
  return `## OTHER TERMS\n\n${preamble}${body}`;
}

// ---------------------------------------------------------------------------
// Required-when-selected validation (2026-06-01 staff request)
// ---------------------------------------------------------------------------

/**
 * Returns the list of human-readable blocking issues that must be resolved
 * before a lease can be generated. An empty array means the form is good to go.
 *
 * Per the 2026-06-01 staff request: when an option is selected that obviously
 * needs a value — a Shared utility's fraction, a custom charge's label, an
 * assigned parking spot's label, or an enabled payment plan's amounts/schedule
 * — staff must not be able to proceed until that value is supplied. Otherwise
 * the lease renders placeholders like "?/?" or dangling blanks.
 *
 * Pure and synchronous so the caller can recompute it on every render and use
 * it to disable the Generate button.
 */
/**
 * A generation-blocking issue. `fieldId` is the DOM id of the offending field
 * (or its section), so the UI can render the warning as a clickable jump-to-field
 * link and colour the field that needs attention.
 */
export interface LeaseBlockingIssue {
  message: string;
  fieldId: string;
}

/**
 * WEL-89: a NON-BLOCKING advisory. Distinct from LeaseBlockingIssue — advisories
 * are surfaced to staff as a warning but DO NOT disable Send. Never feed these
 * into the Send-gate (collectLeaseBlockingIssues / blockingIssues.length === 0).
 */
export interface LeaseAdvisory {
  message: string;
  fieldId: string;
}

/**
 * Collect non-blocking advisories — soft nudges that don't gate Send.
 *
 * Today: carpet is on (the unit has carpet) but no carpet-cleaning fee was
 * entered. That's legitimate (a unit can have carpet with no move-out cleaning
 * fee), so it must NOT block; we just nudge staff to confirm. Kept separate from
 * collectLeaseBlockingIssues precisely so it can never disable Send.
 */
export function collectLeaseAdvisories(s: LeaseFormState): LeaseAdvisory[] {
  const advisories: LeaseAdvisory[] = [];
  const hasCarpet = s.hasCarpet !== false;
  if (hasCarpet && (!Number.isFinite(s.carpetCleaningFee) || s.carpetCleaningFee <= 0)) {
    advisories.push({
      message:
        'Carpet is on but no carpet cleaning fee is set. Add the fee if this unit charges one, or turn carpet off for a hard-surface unit. You can still send.',
      fieldId: 'carpet-fee',
    });
  }
  return advisories;
}

/**
 * Whether an applicant is an adult (age ≥ 18) as of `asOf`. Mirrors the API's
 * `isAdult` (apps/api/src/lib/docusealSubmitters.ts) so the UI gate and the
 * server's adults-only submitter filter agree on who signs.
 *
 * A missing/blank/unparseable DOB is treated as ADULT (include) — we never
 * silently drop a real signer because AppFolio lacks a DOB. The send route then
 * surfaces those via missingDobNames so staff can confirm.
 */
export function isAdultApplicant(
  dateOfBirth: string | null | undefined,
  asOf: Date,
): boolean {
  if (!dateOfBirth || String(dateOfBirth).trim() === '') return true; // missing → include
  const dob = new Date(`${String(dateOfBirth).trim().slice(0, 10)}T00:00:00`);
  if (Number.isNaN(dob.getTime())) return true; // unparseable → include
  let age = asOf.getFullYear() - dob.getFullYear();
  const monthDiff = asOf.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && asOf.getDate() < dob.getDate())) {
    age -= 1;
  }
  return age >= 18;
}

/** Resolve the as-of date for the adults filter: leaseStartDate > today. */
function resolveSignerAsOfDate(s: LeaseFormState): Date {
  const start = s.leaseStartDate;
  if (typeof start === 'string' && start.trim()) {
    const d = new Date(`${start.trim().slice(0, 10)}T00:00:00`);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return new Date();
}

export function collectLeaseBlockingIssues(
  s: LeaseFormState,
  options?: {
    hasGas?: boolean;
    applicants?: ApplicationGroupShape['applicants'];
    /** True for renewal drafts (`renewal-` groupId): enforces the §30 ≥6-month floor. */
    isRenewal?: boolean;
  },
): LeaseBlockingIssue[] {
  const issues: LeaseBlockingIssue[] = [];
  const hasGas = options?.hasGas === true;
  const push = (message: string, fieldId: string) => issues.push({ message, fieldId });

  // Every ADULT signer needs a present + unique email — DocuSeal collides (or
  // fails to route) when two signers share an address, and can't send to a
  // blank one. Minors (age < 18 as of the lease start date) don't sign and are
  // exempt. Mirrors the API's adults-only submitter filter (isAdultApplicant).
  // The offending applicant's email is fixed in AppFolio; we point staff at the
  // Lead applicant card, which lists every signer's email on file.
  if (options?.applicants) {
    const asOf = resolveSignerAsOfDate(s);
    const adults = options.applicants.filter((a) => isAdultApplicant(a.dateOfBirth, asOf));
    const seen = new Map<string, true>();
    let missingFlagged = false;
    let duplicateFlagged = false;
    for (const a of adults) {
      const email = (a.email ?? '').trim();
      if (!email) {
        if (!missingFlagged) {
          push('A signer is missing an email — every signer needs one to receive the lease.', 'lead-applicant-section');
          missingFlagged = true;
        }
        continue;
      }
      const key = email.toLowerCase();
      if (seen.has(key)) {
        if (!duplicateFlagged) {
          push('Each signer needs a unique email — two signers share an address.', 'lead-applicant-section');
          duplicateFlagged = true;
        }
        continue;
      }
      seen.set(key, true);
    }
  }

  // Lease term must be a real term (mirrors the inline §1 error, now blocking).
  if (!Number.isFinite(s.leaseTermMonths) || s.leaseTermMonths < 1) {
    push('Lease term must be at least 1 month.', 'lease-term');
  }

  // §30 builder constraint 2 (renewals only): every renewal is a successive
  // fixed term of ≥MIN_RENEWAL_TERM_MONTHS months. Mirrors the SERVER hard-block
  // (apps/api generate route) so staff see it before generate/send. Only a
  // COMPUTABLE short term blocks — missing/garbled dates are left to the term/
  // end-date inline errors, exactly like the server floor check.
  if (options?.isRenewal && s.leaseStartDate && s.leaseEndDate) {
    const renewalTerm = computeTermFromEndDate(s.leaseStartDate, s.leaseEndDate);
    if (Number.isFinite(renewalTerm) && renewalTerm < MIN_RENEWAL_TERM_MONTHS) {
      push(
        `Renewal term must be at least ${MIN_RENEWAL_TERM_MONTHS} months (this renewal is ${renewalTerm} month(s)). Adjust the lease end date.`,
        'lease-end',
      );
    }
  }

  // §4 early-termination-by-notice: when the option is enabled, its earliest
  // termination date is required (it fills the clause's floor). Mirrors the
  // server validateLeaseState check so staff resolve it before generate/send.
  if (s.earlyTerminationNoticeEnabled && !s.earlyTerminationNoticeEarliestDate) {
    push(
      'Enter the earliest termination date for the Early Termination by Notice option (a calendar month-end).',
      'early-termination-earliest-date',
    );
  }

  // Shared utilities need a complete fraction, or the lease prints "?/?".
  // Skip Sewer (mirrors Water; configured via the combined control) and Gas
  // when the property has no gas (it is filtered out of the rendered lease).
  for (const u of s.utilities) {
    if (u.name === 'Sewer') continue;
    if (u.name === 'Gas' && !hasGas) continue;
    if (u.state !== 'ratio') continue;
    const num = u.shareNumerator;
    const den = u.shareDenominator;
    const complete =
      typeof num === 'number' && Number.isInteger(num) && num >= 1 &&
      typeof den === 'number' && Number.isInteger(den) && den >= 1;
    if (!complete) {
      const label = u.name === 'Water' ? 'Water & Sewer' : u.name;
      push(`${label}: enter the shared fraction (e.g. 1/4) before generating.`, `utility-${u.name}`);
    }
  }

  // A custom "Other monthly charge" needs a label — it becomes the §2 row label.
  if (s.otherCharges.some((c) => c.type === 'custom' && !(c.customLabel ?? '').trim())) {
    push('Enter a label for each custom "Other monthly charge".', 'other-charges');
  }

  // A one-time move-in fee prints its name and amount; a half-filled row would
  // print a blank (or be silently dropped).
  if (
    (s.oneTimeFees ?? []).some((f) => {
      const named = cleanFeeName(f?.name) !== '';
      const priced = typeof f?.amountCents === 'number' && f.amountCents > 0;
      return !named || !priced;
    })
  ) {
    push('Each one-time move-in fee needs both a name and an amount (or remove the row).', 'one-time-fees');
  }

  // Assigned parking interpolates the label into §PARKING; an empty label
  // leaves a dangling blank. Parking config is MFR-only in the builder.
  if (s.propertyType === 'mfr' && s.parking === 'assigned' && !s.parkingAssignedLabel.trim()) {
    push('Enter the assigned parking label (e.g. "Space 12").', 'parking-label');
  }

  // Payment plan, when enabled, must be internally valid (these were inline-only
  // warnings before; now they block generation).
  if (s.paymentPlanEnabled && s.paymentPlan) {
    const plan = s.paymentPlan;
    // Chargeable count, not the raw animalCount — nets the free-animal waiver
    // (matching the server's validateLeaseState, which always did) and RRA
    // per-animal charge exemptions, so the UI ceiling agrees with the server's
    // 409 gate instead of overstating the deposit.
    const depositTotal = refundableDepositTotalCents(s);
    // getPlanTotal reads effectiveCarpetFee (WEL-89), so an off-carpet lease with
    // a stale fee doesn't mask a genuinely empty plan (0 → blocking issue).
    if (getPlanTotal(s) === 0) {
      push('Payment plan is on but has nothing on it — set an amount or turn the plan off.', 'payment-plan');
    }
    if (plan.depositOnPlan > depositTotal) {
      push('Payment plan: the amount on the plan exceeds the total deposit.', 'payment-plan');
    }
    if (typeof plan.carpetFeeOnPlan === 'number' && plan.carpetFeeOnPlan > effectiveCarpetFee(s)) {
      push('Payment plan: the carpet fee on the plan exceeds the carpet cleaning fee.', 'payment-plan');
    }
    for (const fee of effectiveOneTimeFees(s)) {
      if ((plan.feesOnPlan?.[fee.id] ?? 0) > fee.amountCents) {
        push(`Payment plan: the amount on the plan exceeds the ${cleanFeeName(fee.name)}.`, 'payment-plan');
      }
    }
    // A written-request plan's count and dates are set by statute.
    if (
      !plan.tenantWrittenRequest &&
      (plan.numberOfPayments < PAYMENT_PLAN_MIN_PAYMENTS ||
        plan.numberOfPayments > PAYMENT_PLAN_MAX_PAYMENTS)
    ) {
      push(
        `Payment plan: number of payments must be between ${PAYMENT_PLAN_MIN_PAYMENTS} and ${PAYMENT_PLAN_MAX_PAYMENTS}.`,
        'payment-plan',
      );
    }
    if (!plan.tenantWrittenRequest && s.leaseStartDate && plan.firstChargeDate < s.leaseStartDate) {
      push('Payment plan: first charge date must be on or after the lease start date.', 'payment-plan');
    }
  }

  return issues;
}

/**
 * §UTILITIES body — bespoke, assembled from only the pieces that apply. Mirror of
 * the API. Utilities grouped by arrangement (Resident's own, billed-in-full,
 * shared/ratio sub-grouped by fraction, provided/included), each present one
 * emitting a "**Names.** …" line; the general clauses below are modular and each
 * appears only when its trigger is present.
 */
function buildUtilitiesBody(s: LeaseFormState, hasGas: boolean = false): string {
  // Canonical reading order within every grouped line.
  const ORDER = ['Electricity', 'Gas', 'Water', 'Sewer', 'Garbage'];
  const rank = (name: string): number => {
    const i = ORDER.indexOf(name);
    return i === -1 ? ORDER.length : i;
  };
  const utilities = [...normalizeUtilities(s.utilities, { hasGas })].sort(
    (a, b) => rank(a.name) - rank(b.name),
  );
  const namesFor = (state: UtilityState): string[] =>
    utilities.filter((u) => u.state === state).map((u) => u.name);
  const direct = namesFor('tenant_direct');
  const billedFull = namesFor('landlord_billed_full');
  const provided = namesFor('landlord_provided');
  const ratioGroups = new Map<string, string[]>();
  for (const u of utilities) {
    if (u.state !== 'ratio') continue;
    const key = `${u.shareNumerator ?? '?'}/${u.shareDenominator ?? '?'}`;
    ratioGroups.set(key, [...(ratioGroups.get(key) ?? []), u.name]);
  }

  const joinNames = (xs: string[], conj: '&' | 'and'): string =>
    xs.length <= 1
      ? xs[0] ?? ''
      : xs.length === 2
        ? `${xs[0]} ${conj} ${xs[1]}`
        : `${xs.slice(0, -1).join(', ')} ${conj} ${xs[xs.length - 1]}`;

  const blocks: string[] = [];
  if (direct.length) {
    blocks.push(
      `**${joinNames(direct, '&')}.** Resident opens and holds ${direct.length === 1 ? 'this account' : 'these accounts'} in Resident's name and pays the provider directly.`,
    );
  }
  if (billedFull.length) {
    blocks.push(
      `**${joinNames(billedFull, '&')}.** The City bills Lessor; Resident is responsible for the full amount, invoiced by Lessor and paid with rent.`,
    );
  }
  for (const [share, names] of ratioGroups) {
    blocks.push(
      `**${joinNames(names, '&')}.** Resident pays a ${share} share of the monthly charges billed to the Property, invoiced by Lessor and paid with rent.`,
    );
  }
  if (provided.length) {
    blocks.push(`**${joinNames(provided, '&')}.** Provided by Lessor and included in rent.`);
  }

  const general: string[] = [];
  if (direct.length) {
    general.push(
      `For utilities in Resident's name, Resident shall pay the provider without delinquency, provide proof of payment on demand, and maintain service throughout the term; failure to establish or maintain service is a lease violation. If Resident does not establish a service by the date it is required and Lessor must therefore receive and process the utility bills, Resident shall pay an administrative fee of $13 for each and every utility bill Lessor processes.`,
    );
  }
  const billedBack = billedFull.length > 0 || ratioGroups.size > 0;
  if (billedBack) {
    general.push(
      `Utility charges billed to Resident under this Agreement are part of Resident's rent obligation. Non-payment of utility charges is treated the same as non-payment of rent, and Lessor may pursue all remedies available for non-payment of rent, including service of a 14-Day Notice to Pay Rent or Vacate. Lessor may apply Resident's security deposit to recover unpaid utility charges upon move-out.`,
    );
  }
  const meterable = provided.filter((n) => n === 'Electricity' || n === 'Gas' || n === 'Water');
  if (meterable.length) {
    general.push(
      `Where ${joinNames(meterable.map((n) => n.toLowerCase()), 'and')} ${meterable.length === 1 ? 'is' : 'are'} included in rent, Lessor includes ${meterable.length === 1 ? 'it' : 'them'} based on reasonable usage for the Premises. "Reasonable usage" means consumption that does not exceed 125% of the average monthly consumption for the Premises over the preceding thirty-six (36) months (or, where fewer than thirty-six months of usage history exist, over the months for which history is available). Resident is responsible for the cost of any consumption above that amount, as billed by Lessor at the utility provider's rate.`,
    );
  }
  if (billedBack || provided.length > 0) {
    general.push(
      `Lessor and Agent are not liable for failure to provide service or for any losses or damages resulting from utility outages, interruptions, or fluctuations.`,
    );
  }
  if (direct.length > 0 || billedBack) {
    general.push(
      `Any utility or service used at, or made available to, the Premises and not named above is the responsibility of Resident, including any utility or service added or activated after the commencement of this Agreement, unless this Agreement states otherwise.`,
    );
  }

  return [...blocks, ...general].join('\n\n');
}

/**
 * §2 RENT inline payability sentence (kept identical to the legacy
 * renderer constant — see leaseRenderer.ts / render_lease.py history).
 * Inlined into the prose form when §2 has only base rent.
 */
const RENT_PAYABILITY_SENTENCE =
  'payable in advance by the 1st day of each and every month during said ' +
  'term to Lessor at 320 N Colville St, Walla Walla, WA 99362, or any ' +
  'such other place that Lessor may from time to time designate';

/** Full §2 payability paragraph used in table mode; empty in prose mode
 * (the sentence is inlined into the rent paragraph). */
const RENT_PAYABILITY_PARAGRAPH =
  'The total amount set forth above is considered rent and is ' +
  RENT_PAYABILITY_SENTENCE +
  '.';

/**
 * meeting-2026-05-13: build the §2 RENT inline content with all amounts
 * pre-computed. Two modes:
 *  - Prose: only base rent is set → "Resident shall pay monthly rent of $X..."
 *  - Table: any of {animal rent, parking, other charges, HOA fee} present →
 *    per-row table with computed values + Total monthly. Each `otherCharges`
 *    entry becomes its own row (no aggregation).
 *
 * Returns the inline body + an `isTable` flag the caller uses to drive the
 * separate `rent_payability_paragraph` variable (empty in prose mode).
 */
export function buildRentTableInline(args: {
  monthlyRentCents: number;
  animalRentTotalCents: number;
  parkingChargeCents: number;
  otherCharges: OtherCharge[];
  effectiveHoaFeeCents: number;
  monthlyTotalCents: number;
}): { body: string; isTable: boolean } {
  const rows: Array<{ label: string; cents: number }> = [
    { label: 'Monthly premises rent', cents: args.monthlyRentCents },
  ];
  if (args.animalRentTotalCents > 0) {
    rows.push({ label: 'Monthly animal rent', cents: args.animalRentTotalCents });
  }
  if (args.parkingChargeCents > 0) {
    rows.push({ label: 'Parking', cents: args.parkingChargeCents });
  }
  for (const r of args.otherCharges) {
    if (r.amountCents <= 0) continue;
    rows.push({ label: otherChargeLabel(r), cents: r.amountCents });
  }
  if (args.effectiveHoaFeeCents > 0) {
    rows.push({ label: 'HOA Fee', cents: args.effectiveHoaFeeCents });
  }
  if (rows.length === 1) {
    return {
      body:
        `Resident shall pay monthly rent of ${formatMoneyCents(args.monthlyRentCents)}, ` +
        RENT_PAYABILITY_SENTENCE +
        '.',
      isTable: false,
    };
  }
  const tableBody = [
    'Resident shall pay monthly rent and other charges in the following amounts:',
    '',
    '| Charge | Amount |',
    '|---|---:|',
    ...rows.map((r) => `| ${r.label} | ${formatMoneyCents(r.cents)} |`),
    `| **Total monthly** | **${formatMoneyCents(args.monthlyTotalCents)}** |`,
  ].join('\n');
  return { body: tableBody, isTable: true };
}

/** Returns the §2 payability paragraph for table mode, empty string in
 * prose mode (where the sentence is already inlined into the rent paragraph). */
export function buildRentPayabilityParagraph(isTable: boolean): string {
  return isTable ? RENT_PAYABILITY_PARAGRAPH : '';
}

// ---------------------------------------------------------------------------
// §7.4.1: data-driven Approved Animals table generator
// ---------------------------------------------------------------------------

/**
 * Escape a pet field for inline Markdown: collapse newlines (which would break a
 * list item) and backslash-escape the emphasis/link metacharacters, so an odd
 * value like "R*ex" or "[Spot]" can't turn into bold/italic or a link.
 */
function mdInline(v: string): string {
  return v
    .replace(/\r?\n/g, ' ')
    .replace(/([\\*_`[\]])/g, '\\$1')
    .trim();
}

/**
 * Build the Approved Animals roster for §7 as a Markdown bullet LIST — one item
 * per pet, `- **Name** — Type · Weight · Age`, populated from the AppFolio Pets
 * snapshot via petToRow (the single shape-dependent boundary).
 *
 * A list, not a table, on purpose: the §7 body is a two-column flow, and a
 * Markdown table renders as a `<table>` that matches `.lease-body > table`
 * (column-span: all) and breaks out to the full page width — wrong for a short
 * data roster, and inconsistent across WeasyPrint versions. A `<ul>` flows
 * inline in the column like the surrounding prose. No DocuSeal fillable tags:
 * residents no longer fill the roster at signing; it comes straight from the
 * application data.
 *
 * Each item leads with the pet's name (bold), falling back to the type when the
 * name is absent so a line is never an empty bullet. Returns '' for an empty
 * snapshot (the section is not compiled in when no animals are permitted). The
 * function name is retained for call-site stability though the output is a list.
 */
export function buildApprovedAnimalsTable(pets: AppFolioPet[]): string {
  const items = (pets ?? []).map((pet) => {
    const r = petToRow(pet);
    const name = mdInline(r.name);
    const type = mdInline(r.type);
    const weight = mdInline(r.weight);
    const age = mdInline(r.age);
    // Enrichment / intake detail fields. Each renders only when collected, so a
    // roster with only Name/Type/Weight/Age yields exactly the previous output.
    const breed = mdInline(r.breed);
    const color = mdInline(r.color);
    const gender = mdInline(r.gender);
    const spayedNeutered = mdInline(r.spayedNeutered);
    // "License #12345" when a number is present; the gentle phrasing avoids a
    // bare number reading as something else. License never blocks the lease, so
    // an absent number simply drops the segment.
    const license = r.license ? `License #${mdInline(r.license)}` : '';
    const lead = name || type;
    // Detail order: type (when a name led), then breed, color, weight, age,
    // gender, spay/neuter, license — "as much as we have collected", omitting
    // blanks.
    const detail = (name ? [type] : [])
      .concat([breed, color, weight, age, gender, spayedNeutered, license])
      .filter(Boolean)
      .join(' · ');
    let line = '-';
    if (lead) line += ` **${lead}**`;
    if (detail) line += lead ? ` — ${detail}` : ` ${detail}`;
    return line;
  });
  return items.join('\n');
}

/** "A" / "A and B" / "A, B and C" (no serial comma — the pending-decision clause's wording). */
function joinNamesAnd(parts: string[]): string {
  if (parts.length <= 1) return parts.join('');
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/** Non-negative whole cents from a staff-entered amount (0 for anything else). */
function pendingCents(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.round(v)) : 0;
}

/**
 * RRA v2.2 part B (WEL-180, Miles 2026-10-30): the pending-decision clause printed
 * after the Approved Animals list while an accommodation request about one or more
 * of the household's animals is open. Wording is verbatim from the approved sheet;
 * {names} are the matched animals as the list prints them, {rent} / {deposit} the
 * SUM of their if-denied amounts. Nothing about the request itself appears. '' when
 * no animal is pending, so every existing lease renders byte-identically.
 */
export function buildAnimalDecisionPendingParagraph(
  s: Pick<LeaseFormState, 'pets' | 'animalsPendingDecision'>,
): string {
  const matched = matchPendingDecisionAnimals(s);
  if (matched.length === 0) return '';
  const names = joinNamesAnd(
    matched.map(
      ({ entry, pet }) => mdInline(petToRow(pet).name) || mdInline(String(entry.name ?? '')) || 'an animal',
    ),
  );
  const rent = matched.reduce((sum, { entry }) => sum + pendingCents(entry.ifDeniedRentCents), 0);
  const deposit = matched.reduce((sum, { entry }) => sum + pendingCents(entry.ifDeniedDepositCents), 0);
  return (
    `Animal rent and the animal security deposit for ${names} are $0 while Lessor's decision is pending. ` +
    `If Lessor does not waive them, animal rent of ${formatMoneyCents(rent)} per month and an animal security ` +
    `deposit of ${formatMoneyCents(deposit)} apply beginning the first of the month after Lessor's written ` +
    `notice of the decision.`
  );
}

// ---------------------------------------------------------------------------
// RESIDENT DESIGNATION details (§ designation) — pre-lease intake wiring
// ---------------------------------------------------------------------------

// (Removed, WEL-6/WEL-7) The blank-fill RESIDENT DESIGNATION table that emitted
// DocuSeal {{...;type=text}} signer fields is GONE: the finished lease must have
// ZERO signer-fillable fields. The designation is always collected pre-lease
// (intake or the staff builder, enforced by INTAKE_ENFORCE_DESIGNATION) and baked
// in as plain text by buildResidentDesignationDetails below.

/** HTML-escape a designee value so it can't inject markup into the table cell. */
function htmlEscape(v: string): string {
  return v
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Build the RESIDENT DESIGNATION block for `{{ resident_designation_details }}`.
 * The finished lease has ZERO signer-fillable fields, so this NEVER emits DocuSeal
 * tags: the designation is collected pre-lease (intake or the staff builder) and
 * baked in as a plain CONTACT BLOCK — name (relationship) / mailing address /
 * phone · email. Empty fields drop out. With no designation on file (test-lease or
 * un-enforced preview only) it renders a neutral placeholder, never fill fields.
 * See WEL-6/WEL-7.
 */
export function buildResidentDesignationDetails(
  d: ResidentDesignation | null | undefined,
): string {
  const name = (d?.designeeName ?? '').trim();
  // No designation on file — only reachable for synthetic test-leases or an
  // un-enforced preview (real generation hard-blocks without one). NEVER emit
  // signer-fillable DocuSeal fields; render a neutral baked placeholder.
  if (!name) return '<div class="designee-block"><div class="d-name">(No resident designation on file)</div></div>';
  // Provided (intake/staff): a clean contact block — name (relationship) /
  // mailing address / phone · email — NOT the fill-in form. Empty fields drop
  // out so a partial designation never shows blank labelled lines.
  const rel = (d?.relationship ?? '').trim();
  const addr = (d?.mailingAddress ?? '').trim();
  const contact = [(d?.phone ?? '').trim(), (d?.email ?? '').trim()]
    .filter(Boolean)
    .map(htmlEscape)
    .join(' · ');
  const nameLine = rel ? `${htmlEscape(name)} (${htmlEscape(rel)})` : htmlEscape(name);
  const lines = [
    `<div class="d-name">${nameLine}</div>`,
    addr ? `<div class="d-addr">${htmlEscape(addr)}</div>` : '',
    contact ? `<div class="d-contact">${contact}</div>` : '',
  ].filter(Boolean);
  return `<div class="designee-block">\n${lines.join('\n')}\n</div>`;
}

/**
 * RESIDENT DESIGNATION intro paragraph for `{{ resident_designation_intro }}`,
 * count-conditional (RCW 59.18.590 covers a deceased SOLE occupant). A single
 * resident keeps the original wording byte-identically; with >=2 residents the
 * designation is scoped so it takes effect only when no co-Resident survives in
 * occupancy (a surviving roommate handles the Premises instead). resident_names
 * and premises_address are inlined — the key is in UNTRUSTED_PREFILL_KEYS so the
 * DocuSeal-tag injection guard still covers it.
 */
export function buildResidentDesignationIntro(
  residentNames: string,
  premisesAddress: string,
  residentCount: number,
): string {
  if (residentCount >= 2) {
    return `The undersigned, ${residentNames}, Residents at ${premisesAddress}, hereby designate the individual identified below ("Designee") to act on a deceased Resident's behalf pursuant to RCW 59.18.590. This designation applies only when a Resident dies while the sole remaining occupant of the Premises; that is, when no other Resident under this Agreement survives and remains in occupancy. So long as any other Resident remains in occupancy, that surviving Resident, and not the Designee, is responsible for the Premises and for a deceased Resident's personal property under this Agreement, and this designation does not take effect. (Residents must choose as Designee a person who does not reside at the Premises.)`;
  }
  return `The undersigned, ${residentNames}, Resident at ${premisesAddress}, hereby designates the individual identified below ("Designee") as the person to act on Resident's behalf pursuant to RCW 59.18.590 in the event that Resident is the sole occupant of the Premises on the date of Resident's death. (Resident must choose as Designee a person who does not reside at the Premises.)`;
}

/**
 * RESIDENT DESIGNATION closing paragraph for `{{ resident_designation_closing }}`
 * — the per-signer access grant. With >=2 residents the grant is conditioned on
 * no other Resident remaining in occupancy, matching the count-conditional intro.
 * Single-resident wording is byte-identical to the original.
 */
export function buildResidentDesignationClosing(residentCount: number): string {
  const condition =
    residentCount >= 2
      ? ' and provided no other Resident then remains in occupancy of the Premises,'
      : '';
  return `I hereby sign that upon my death,${condition} Designee is allowed to have access to the Premises, remove my personal property, receive funds due me from the Lessor, and dispose of my property consistent with my last will and testament and any applicable intestate succession law. I understand that this designation will remain in effect until revoked by me or replaced with a new designation. I also understand that I may change Designee or revoke this designation in writing at any time prior to my death.`;
}

// Spec §24.3 / §30 builder-constraint 4 (ANIM-03): buildAnimalRaPendingParagraph
// (the §APPROVED ANIMALS RA-pending closing paragraph) was removed. Its prose
// anchor {{ animal_ra_pending_paragraph }} is gone from animals_permitted.md and
// its builder slot is gone from buildRenderConfig; RA suspension behavior now
// lives entirely in WHP accommodation policy / AppFolio workflow, not the lease.
// WEL-104: the leftover `animalRaPending` / `animalRaCount` staff fields (the
// builder checkbox that fed them) were also removed — RA handling is internal
// per Rule H, tracked by a separate RRA workflow. Mirror of
// apps/api/src/lib/leaseConfig.ts — keep in lock-step.

// ---------------------------------------------------------------------------
// §10: utility responsibility binary mapping + contacts table generator
// ---------------------------------------------------------------------------

/**
 * Map a UtilityState to its binary responsibility cell value (§10.2).
 *
 * - tenant_direct → Resident (Resident sets up account, pays provider directly)
 * - ratio → Resident (Resident bears an allocated share of the bill)
 * - landlord_billed_full → Lessor (pass-through, but Lessor owns the
 *   tenant-facing relationship)
 * - landlord_provided → Lessor (included in rent)
 *
 * The per-utility legal paragraphs (utility_state_*.md) carry the
 * billing-arrangement substance; this helper just identifies who pays
 * for the binary table.
 */
export function utilityResponsibility(state: UtilityState): 'Lessor' | 'Resident' {
  return state === 'tenant_direct' || state === 'ratio' ? 'Resident' : 'Lessor';
}

/**
 * Build the two-column UTILITY CONTACTS table per §10.3.
 *
 * Returns `{ table, include }`:
 * - `include` is `true` iff at least one utility is `tenant_direct` (the gate
 *   per §10.7 — when no utility is tenant-direct, the whole UTILITY CONTACTS
 *   section is suppressed and `utility_contacts.md` is not compiled in).
 * - `table` is a pre-rendered Markdown two-column table when `include`,
 *   empty string otherwise.
 *
 * Left column ("Resident Contacts"): one row per `tenant_direct` utility,
 * formatted `"<Name> — <provider.name>, <provider.phone>"`. Provider is
 * resolved by `providerFor()` (§10.4 mapping). If `providerFor()` returns
 * null (e.g., water/sewer set to tenant_direct, which §10.5 says should
 * never happen in WHP's market), the function throws — staff sees the
 * error in preview before generating, instead of a "provider TBD" placeholder
 * leaking into a tenant-facing lease.
 *
 * Right column ("Lessor Manages"): one row per non-`tenant_direct` utility
 * (covers `landlord_billed_full`, `landlord_provided`, and `ratio` —
 * Resident contacts no one in any of those states).
 *
 * Shorter column is padded with empty strings so both columns have equal
 * height. In WHP's Walla Walla market, water and sewer are always Lessor-
 * managed so the right column is always non-empty when the section renders.
 *
 * Inline JSDoc-documented test cases (manual smoke per Phase A spec gap #7):
 *   - Mix (Electricity tenant_direct, others landlord_*) →
 *       left: 1 entry "Electricity — Pacific Power, 888.221.7070";
 *       right: rest; padded so heights match.
 *   - All-tenant-direct →
 *       left: all 5 entries with providerFor() lookups; right: empty
 *       (padded with empty strings); include = true.
 *   - All-landlord-* (no tenant_direct, no ratio) →
 *       include = false; table = ''.
 *   - All-ratio (no tenant_direct) →
 *       include = false; table = ''. (Ratio is Resident in §10.2 but
 *       not tenant_direct, so contacts section is hidden — Resident
 *       contacts no provider.)
 */
export function buildUtilityContactsTable(
  utilities: UtilityConfig[],
  electricalProvider: 'Pacific Power' | 'Columbia REA' | null,
  leaseStartDate: string,
  garbageProvider?: string | null,
): { table: string; include: boolean } {
  // UTILITY CONTACTS side-by-side grid: RESIDENT sets up (left, with the
  // lease-start deadline + $10 processing fee) vs LESSOR manages (right,
  // do-not-contact + bill-back explanation). Mirror of the API. Raw HTML →
  // `.util-grid` flex; host exhibit forced single-column.
  const residentItems: string[] = [];
  for (const u of utilities) {
    if (u.state !== 'tenant_direct') continue;
    const provider = providerFor(u.name, electricalProvider, garbageProvider);
    if (provider) {
      const logo = `<img class="util-logo" src="assets/${provider.logo}" alt="${provider.name}" />`;
      residentItems.push(`<li>${logo}${u.name} — ${provider.name}, ${provider.phone}</li>`);
      continue;
    }
    if (u.name.trim().toLowerCase().startsWith('garbage')) {
      // AppFolio has no confirmed hauler for this property — stay GENERAL (no
      // specific name/phone); the resident still arranges their own garbage
      // service. The matching instruction block in utility_contacts.md is
      // likewise generic.
      residentItems.push(
        `<li>${u.name} — set up service with the garbage provider for the property</li>`,
      );
      continue;
    }
    // Water/Sewer tenant_direct = a misconfiguration (never tenant-set-up here).
    // eslint-disable-next-line no-console
    console.warn(
      `[utility_contacts] ${u.name} is tenant_direct but no provider is mapped — rendering placeholder. Per §10.5 water/sewer should be landlord-managed; fix the utility configuration before generating.`,
    );
    residentItems.push(`<li>${u.name} — ⚠️ fix utility configuration (set to Lessor-managed)</li>`);
  }
  if (residentItems.length === 0) {
    return { table: '', include: false };
  }

  const lessorItems: string[] = [];
  const billedBack: string[] = [];
  for (const u of utilities) {
    if (u.state === 'tenant_direct') continue;
    lessorItems.push(`<li>${u.name}</li>`);
    if (u.state === 'ratio' || u.state === 'landlord_billed_full') billedBack.push(u.name);
  }

  const startDate = leaseStartDate ? fmtDateLong(leaseStartDate) : 'the first day of the lease term';
  const joinAnd = (xs: string[]): string =>
    xs.length <= 1
      ? xs[0] ?? ''
      : xs.length === 2
        ? `${xs[0]} and ${xs[1]}`
        : `${xs.slice(0, -1).join(', ')}, and ${xs[xs.length - 1]}`;

  const billedBackPara = billedBack.length
    ? `<p>${joinAnd(billedBack)} ${billedBack.length === 1 ? 'is' : 'are'} billed back to you: Lessor receives the invoice from the provider and bills your share to you, payable with your rent. Direct any billing questions to Lessor — never the provider.</p>`
    : '';
  const lessorBody = lessorItems.length
    ? `<ul class="util-list">${lessorItems.join('')}</ul>`
    : '<p>None — every utility is set up by Resident.</p>';

  const grid =
    '<div class="util-grid">' +
    '<div class="util-col util-resident">' +
    '<p class="util-head">Resident sets up</p>' +
    `<p>Open an account in your name with each provider below, effective <strong>${startDate}</strong> — your lease start date. If you do not put a utility in your name by its due date and Lessor has to process the bill, a $13 administrative fee applies to each and every utility bill Lessor processes.</p>` +
    `<ul class="util-list">${residentItems.join('')}</ul>` +
    '</div>' +
    '<div class="util-col util-lessor">' +
    '<p class="util-head">Lessor manages — do not contact</p>' +
    "<p>These stay in Lessor's name. Do not contact these providers, and do not open, change, transfer, or close any of these accounts.</p>" +
    lessorBody +
    billedBackPara +
    '</div>' +
    '</div>';

  return { table: grid, include: true };
}

// ---------------------------------------------------------------------------
// buildRenderConfig — builds the JSON the Python renderer expects
// ---------------------------------------------------------------------------

/**
 * Build the generated-PDF filename per WHP convention:
 *   <last names>-MMYYYY[-anml][-PP][-HOA].pdf
 *
 * Names: all approved applicants' last names, deduplicated, in group order.
 *   - 1 → "Smith"
 *   - 2 → "Smith & Jones"
 *   - 3+ → "Smith, Jones & Davis"
 *
 * Then spaces and commas inside the assembled filename are converted to
 * hyphens (the user-selected separator), runs of hyphens collapsed.
 *
 * Date: MMYYYY of the lease start date.
 *
 * Flags: anml (animals === 'yes'), PP (paymentPlanEnabled), HOA
 * (hoaSource !== 'none').
 */
export function buildLeasePdfFilename(
  g: ApplicationGroupShape,
  s: LeaseFormState,
): string {
  const lastNames: string[] = [];
  for (const a of g.applicants) {
    const ln = (a.lastName ?? '').trim();
    if (!ln) continue;
    if (!lastNames.includes(ln)) lastNames.push(ln);
  }

  let nameBlock: string;
  if (lastNames.length === 0) {
    nameBlock = 'Lease';
  } else if (lastNames.length === 1) {
    nameBlock = lastNames[0]!;
  } else {
    const head = lastNames.slice(0, -1);
    const tail = lastNames[lastNames.length - 1]!;
    nameBlock = `${head.join(', ')} & ${tail}`;
  }

  const ymd = s.leaseStartDate || '';
  const date = /^\d{4}-\d{2}-\d{2}$/.test(ymd)
    ? `${ymd.slice(5, 7)}${ymd.slice(0, 4)}`
    : '';

  const flags: string[] = [];
  if (s.animals === 'yes') flags.push('anml');
  if (s.paymentPlanEnabled) flags.push('PP');
  if (s.hoaSource !== 'none') flags.push('HOA');

  const blocks = [nameBlock];
  if (date) blocks.push(date);
  blocks.push(...flags);

  const stem = blocks
    .join('-')
    .replace(/[ ,]/g, '-')
    .replace(/-+/g, '-');
  return `${stem}.pdf`;
}

/**
 * Build the lease render config JSON consumed by the Python renderer.
 *
 * Optional `effectiveHoaDefaultFeeCents` (meeting-2026-05-13 §3.3): caller
 * resolves the property's associated HOA via HoaRepo and passes its
 * `defaultFeeCents`. The form's `hoaFeeOverrideCents`, if set, takes
 * precedence; otherwise this default is used. Param is optional + nullable
 * so the web-side preview caller (no DynamoDB access) can omit it.
 *
 * The signature change is intentionally backward-compatible: existing
 * callers that pass two args continue to work — they simply do not get
 * the HOA fee row in §2 / §40 unless an override is set.
 *
 * Optional `options.includeYardCare` (RENEWAL policy): renewal leases drop the
 * YARD CARE ("lawn care hiring") section entirely. When false, the `yard_care`
 * module is emitted as '' and `include_yard_care: false` is set so the renderer
 * compiles in nothing for [[YARD_CARE_MODULE]]. Defaults true — new-lease and
 * test flows (and every existing caller / the golden harness) are unchanged.
 *
 * SYNC WITH apps/api/src/lib/leaseConfig.ts.
 */
export function buildRenderConfig(
  g: ApplicationGroupShape,
  s: LeaseFormState,
  effectiveHoaDefaultFeeCents?: number | null,
  options?: { includeYardCare?: boolean },
) {
  const includeYardCare = options?.includeYardCare !== false;
  // Resident names: all approved applicants joined " and ".
  const includedNames = g.applicants
    .map((a) => `${a.firstName} ${a.lastName}`.trim())
    .filter(Boolean);
  const residentNames = includedNames.join(' and ');

  // The applicant count drives the number of resident signers. The upper
  // bound is just a defensive ceiling against malformed data (a corrupt group
  // shouldn't produce a 50-row signature block) — not a platform limit, since
  // the renderer (Resident{k} tags) and DocuSeal submitters scale dynamically.
  const residentCount = Math.max(1, Math.min(12, includedNames.length));

  // Premises address: editable in the form; defaults to unit address.
  const premisesAddress = s.premisesAddress;

  // §6: Animal toggle drives component selection and deposit/rent roll-up.
  // meeting-2026-05-13 §2.1: per-animal contributions scale by the chargeable
  // count, not the raw animalCount. chargeableAnimalCount nets the manual
  // free-animal waiver, per-animal RRA charge exemptions, and the RRA
  // pending-hold suspension — each waives BOTH the per-animal monthly rent and
  // the per-animal deposit contribution.
  const animalsPermitted = s.animals === 'yes';
  const chargeableAnimals = chargeableAnimalCount(s);
  const animalRentTotal = animalsPermitted ? chargeableAnimals * s.animalRentPerAnimal : 0;
  const animalDepositTotal = animalsPermitted ? chargeableAnimals * s.animalDepositPerAnimal : 0;

  // §6: The singular Refundable Security Deposit = base + animal contribution.
  const securityDepositTotal = s.securityDeposit + animalDepositTotal;

  // §4: Other charges.
  const otherChargesTotal = s.otherCharges.reduce((sum, r) => sum + r.amountCents, 0);
  const otherChargesDesc = s.otherCharges
    .map((r) => `${otherChargeLabel(r)}: ${formatMoneyCents(r.amountCents)}`)
    .join('; ');

  // meeting-2026-05-13 §3.1: Parking charge (when parkingCharged === true
  // and amount > 0) becomes a recurring monthly line item in §2.
  const parkingChargeCents =
    s.parkingCharged && (s.parkingChargeCents ?? 0) > 0 ? (s.parkingChargeCents ?? 0) : 0;

  // meeting-2026-05-13 §3.3: HOA fee — override takes precedence over the
  // HOA's default (resolved by the caller). null/undefined override and
  // null/undefined default both → no HOA row.
  const hoaOverride = s.hoaFeeOverrideCents;
  const effectiveHoaFeeCents =
    hoaOverride != null
      ? Math.max(0, hoaOverride)
      : Math.max(0, effectiveHoaDefaultFeeCents ?? 0);

  // §3: Lease end date from persisted leaseEndDate (step 9 C). Set by
  // buildDefaultFormState on initial load; Phase B's §3 reducer keeps it in
  // sync with leaseStartDate / leaseTermMonths.
  const leaseEndDate = s.leaseEndDate;

  // Module file selection — drives which markdown components compile in.
  const animalModule = animalsPermitted ? 'animals_permitted.md' : 'animals_not_permitted.md';
  const propertyTypeModule = s.propertyType === 'mfr' ? 'property_type_mfr.md' : 'property_type_sfr.md';
  // meeting-2026-05-13 §7: SFR leases always use parking_sfr.md (driveway/
  // garage + general vehicle rules). MFR keeps the assigned/unassigned/
  // no_parking selector.
  const parkingModule =
    s.propertyType === 'sfr'
      ? 'parking_sfr.md'
      : s.parking === 'none'
      ? 'parking_no_parking.md'
      : s.parking === 'assigned'
      ? 'parking_assigned.md'
      : 'parking_unassigned.md';
  // Renewal leases omit the YARD CARE section entirely (includeYardCare:false).
  // For every other lease the module is selected from the responsibility split.
  const yardCareModule = !includeYardCare
    ? ''
    : s.yardCare === 'resident'
      ? 'yard_care_resident_responsible.md'
      : 'yard_care_landlord_responsible.md';
  const mfrSubtype =
    s.propertyType === 'mfr'
      ? s.hasOutdoorSpace
        ? 'mfr_private_yard.md'
        : 'mfr_no_private_yard.md'
      : null;

  // WEL-89: the carpet fee that ACTUALLY applies — 0 when carpet is toggled off,
  // even if a stale carpetCleaningFee lingers in state. The plan and the summary
  // read it through moveInCharges, so an off-carpet lease can never show a
  // carpet fee or emit [[REF:carpet_fee]] for a section that isn't rendered.
  const carpetFee = effectiveCarpetFee(s);

  // §6.7: monthly_total is the recurring monthly sum (base rent + animal rent
  // + other charges + parking + HOA). Used by financial_summary.md
  // "Rent & other charges" row and by Recurring Monthly Charges section
  // gating below.
  // Defensive: coerce each addend to a finite number. Legacy drafts can
  // carry undefined values for fields added in the meeting-2026-05-13
  // schema bump (freeAnimalCount, parkingChargeCents, hoaFeeOverrideCents);
  // a single undefined poisons the total to NaN.
  const safeCents = (n: unknown): number =>
    typeof n === 'number' && Number.isFinite(n) ? n : 0;
  const monthlyTotal =
    safeCents(s.monthlyRent) +
    safeCents(animalRentTotal) +
    safeCents(otherChargesTotal) +
    safeCents(parkingChargeCents) +
    safeCents(effectiveHoaFeeCents);

  // §5: Utility ratio — emit ratio string when state === 'ratio'. Always
  // built from the normalized list so Sewer mirrors Water for the renderer.
  // Gas filtered out when the unit's AF "Has Gas?" is not explicitly true.
  const hasGasOnProperty = g.unit?.hasGas === true;
  const utilitiesForRender = normalizeUtilities(s.utilities, { hasGas: hasGasOnProperty }).map((u) => ({
    name: u.name,
    state: u.state,
    ...(u.state === 'ratio'
      ? { ratio: `${u.shareNumerator ?? '?'}/${u.shareDenominator ?? '?'}` }
      : {}),
  }));

  // meeting-2026-05-13 §3 / §4: Recurring Monthly Charges section is the
  // pre-rendered §2 RENT-adjacent table. Per spec it includes base rent,
  // pet rent (chargeable × per-animal rate), parking (when charged), one
  // row per `otherCharges` entry (skipping zero amounts), and the HOA fee
  // when effective fee > 0. Section collapses when base rent is the only
  // recurring charge (no animal/parking/other/HOA rows) — avoids restating
  // information already in §2 of core_lease.md.
  const recurringRows: Array<{ label: string; cents: number }> = [
    { label: 'Monthly premises rent', cents: s.monthlyRent },
  ];
  if (animalRentTotal > 0) {
    recurringRows.push({ label: 'Monthly animal rent', cents: animalRentTotal });
  }
  if (parkingChargeCents > 0) {
    recurringRows.push({ label: 'Parking', cents: parkingChargeCents });
  }
  for (const r of s.otherCharges) {
    if (r.amountCents <= 0) continue;
    recurringRows.push({ label: otherChargeLabel(r), cents: r.amountCents });
  }
  if (effectiveHoaFeeCents > 0) {
    recurringRows.push({ label: 'HOA Fee', cents: effectiveHoaFeeCents });
  }
  const recurringMonthlyChargesSection =
    recurringRows.length <= 1
      ? ''
      : [
          '### Recurring Monthly Charges',
          '',
          '| Charge | Amount |',
          '|---|---:|',
          ...recurringRows.map((r) => `| ${r.label} | ${formatMoneyCents(r.cents)} |`),
          `| **Total monthly** | **${formatMoneyCents(monthlyTotal)}** |`,
        ].join('\n');

  // §2 RENT inline body — pre-rendered with concrete amounts so the
  // renderers don't need to interpolate `{{ monthly_total }}` /
  // `{{ other_monthly_charges }}` placeholders that no longer exist as
  // single scalar variables (per meeting-2026-05-13: one row per
  // otherCharges entry, computed total inline).
  const rentTableInline = buildRentTableInline({
    monthlyRentCents: s.monthlyRent,
    animalRentTotalCents: animalRentTotal,
    parkingChargeCents,
    otherCharges: s.otherCharges,
    effectiveHoaFeeCents,
    monthlyTotalCents: monthlyTotal,
  });

  // §10: utility responsibility binary mapping + Mode A/B section.
  // The whole §UTILITIES body (utilities_framework.md), assembled bespoke from
  // only the arrangements present + the general clauses they trigger.
  const utilitiesBody = buildUtilitiesBody(s, hasGasOnProperty);

  // Normalized utility list (Water+Sewer synced, Gas filtered when not present) —
  // used by the UTILITY CONTACTS grid and the per-utility responsibility scalars.
  const normalizedUtilities = normalizeUtilities(s.utilities, { hasGas: hasGasOnProperty });

  const anyTenantDirect = normalizedUtilities.some((u) => u.state === 'tenant_direct');
  const utilityContactsReference = anyTenantDirect
    ? `For utilities Resident must establish directly, see **UTILITY CONTACTS** at the end of this Agreement for provider names and phone numbers.`
    : '';

  const contacts = buildUtilityContactsTable(
    normalizedUtilities,
    g.property?.electricalProvider ?? null,
    s.leaseStartDate,
    g.property?.garbageProvider ?? null,
  );

  // SUMMARY OF AMOUNTS DUE + MOVE-IN PAYMENT PLAN + MOVE-IN FEES, all from one
  // model (docs/move-in-payment-plan-plan.md): the deposit due upon signing,
  // everything else due on or before the move-in date, plan payments after
  // move-in owed in addition to the total.
  const amountsDue = computeAmountsDue(s, effectiveHoaFeeCents);
  const planParts = buildPaymentPlanSectionParts(s, amountsDue);
  const moveInFeesBody = buildMoveInFeesBody(amountsDue);

  return {
    // Footer document code: {property type} {signer count}.{form version}, e.g.
    // "MFR 2.15" = a 2-signer multi-family lease, form revision 15. Bump the
    // trailing version when the lease form revision changes.
    document_code: `${s.propertyType === 'mfr' ? 'MFR' : 'SFR'} ${residentCount}.16`,
    title: s.propertyType === 'mfr' ? 'Multi-Family Lease Agreement' : 'Single-Family Lease Agreement',
    cover_image: s.propertyType === 'mfr' ? 'assets/mfr_cover.jpg' : 'assets/sfr_cover.jpg',
    // Drives DocuSeal resident-tag expansion (one Resident{k} role per
    // resident); see the residentCount derivation above and
    // lease_render/render_lease.py.
    resident_count: residentCount,
    // Per-resident signer names in Resident{k} order so the renderer labels each
    // signature block with the actual name (cosmetic; no role/order change).
    resident_signers: includedNames,
    include_lbp: s.includeLbp,
    // Phase E Codex B1: derive from canonical hoaSource, not cached hasHoa.
    include_hoa: deriveHasHoa(s.hoaSource),
    include_outdoor_space: s.hasOutdoorSpace,
    include_sfr_areas_not_included: s.propertyType === 'sfr' && s.yardCare === 'resident',
    // §4 negotiated no-fee early-termination-by-notice option. Gate on BOTH the
    // enable flag AND a configured earliest date so a half-configured state never
    // renders the clause with a blank floor (keeps the live client preview clean;
    // generation is also blocked by collectLeaseBlockingIssues + the server).
    include_early_termination_notice:
      s.earlyTerminationNoticeEnabled && !!s.earlyTerminationNoticeEarliestDate,
    // WEL-89: whether the unit has carpet (the last numbered section). Coalesce an
    // absent value to true so legacy drafts (snapshotted before hasCarpet existed)
    // render carpet exactly as before. When false, both renderers drop the CARPET
    // section + its trailing number.
    has_carpet: s.hasCarpet !== false,
    // RENEWAL policy: renewal leases drop the YARD CARE section. Emit the key ONLY
    // in the drop case (`false`); the include case omits it, since both renderers
    // default an ABSENT key to "include" (Python `config.get(..., True)`; web
    // `config.include_yard_care !== false`). HTML is byte-identical either way — the
    // golden harness renders HTML, not the config JSON — this just keeps the common
    // non-renewal config tidy. (Was unconditionally `true` for every non-renewal.)
    ...(includeYardCare ? {} : { include_yard_care: false }),
    // §10.7: gates whether utility_contacts.md compiles in. true iff at
    // least one utility is tenant_direct.
    include_utility_contacts: contacts.include,
    include_other_monthly_charges: otherChargesTotal > 0,
    include_animal_rent: animalRentTotal > 0,
    // §9.9: gates whether payment_plan.md compiles in at step 16.5.
    // Requires planTotal > 0 in addition to the enable flag — without this
    // gate, a staff user could enable the plan, set depositOnPlan=0 and
    // includeCarpetFee=false, and ship a degenerate "SECURITY DEPOSIT
    // PAYMENT PLAN" section with an empty inclusion bullet list and an
    // empty schedule table. Inline UI surfaces the error
    // (PaymentPlanSection's planTotalZero check) but doesn't mechanically
    // block generation; this gate provides the renderer-side backstop.
    include_payment_plan: s.paymentPlanEnabled && getPlanTotal(s) > 0,
    // MOVE-IN FEES section: only when a staff-added one-time fee applies.
    include_move_in_fees: effectiveOneTimeFees(s).length > 0,
    modules: {
      animal_policy: animalModule,
      property_type: propertyTypeModule,
      ...(mfrSubtype ? { mfr_subtype: mfrSubtype } : {}),
      parking: parkingModule,
      yard_care: yardCareModule,
    },
    utilities: utilitiesForRender,
    variables: {
      resident_names: residentNames,
      premises_address: premisesAddress,
      document_date: fmtDateLong(s.documentDate),
      lease_start_date: fmtDateLong(s.leaseStartDate),
      lease_start_date_short: fmtDateShort(s.leaseStartDate),
      lease_end_date: fmtDateLong(leaseEndDate),
      // §4 early-termination-by-notice floor (long-form). '' when not configured;
      // the clause only renders when include_early_termination_notice is set.
      early_termination_earliest_date: s.earlyTerminationNoticeEarliestDate
        ? fmtDateLong(s.earlyTerminationNoticeEarliestDate)
        : '',
      lease_end_date_short: fmtDateShort(leaseEndDate),
      lease_term_months: String(s.leaseTermMonths),
      monthly_rent: formatMoneyCents(s.monthlyRent),
      animal_rent: formatMoneyCents(animalRentTotal),
      other_monthly_charges: formatMoneyCents(otherChargesTotal),
      other_monthly_description: otherChargesDesc,
      security_deposit: formatMoneyCents(securityDepositTotal),
      // WEL-89: effectiveCarpetFee (0 when carpet off) — the CARPET section that
      // consumes this is dropped when carpet is off, so the value never reaches
      // HTML in that case, but emitting 0 keeps it honest regardless.
      carpet_cleaning_fee: formatMoneyCents(carpetFee),
      // Legacy fallback shims for any registry references that survived
      // the meeting-2026-05-13 cleanup. `non_refundable_fees` /
      // `listed_security_deposit` remain because they are still consumed
      // elsewhere (Adobe Sign tag registry traceability). The retired
      // placeholders `monthly_total`, `total_due`, `non_refundable_fee_rows`,
      // `refundable_deposit_rows`, and `utility_charges_section` are dropped
      // — the markdown no longer references them.
      non_refundable_fees: formatMoneyCents(carpetFee),
      listed_security_deposit: formatMoneyCents(securityDepositTotal),
      // §6.7: pre-rendered Recurring Monthly Charges section (or '' when only
      // base rent applies — collapses the section so the PDF doesn't redundantly
      // restate the rent value already shown in Section 2 of core_lease.md).
      // Now includes parking, per-row other charges, and HOA fee per the
      // meeting-2026-05-13 spec.
      recurring_monthly_charges_section: recurringMonthlyChargesSection,
      // §2 RENT inline: full pre-rendered body with all amounts baked in
      // (no `{{ }}` placeholders need the renderer's second-pass substitution).
      // One row per `otherCharges` entry; computed `Total monthly` line. Prose
      // mode (single sentence) when only base rent applies.
      rent_table_inline: rentTableInline.body,
      // Companion paragraph that follows the §2 table. Non-empty only in
      // table mode; empty in prose mode (the payability sentence is already
      // inlined in the rent paragraph).
      rent_payability_paragraph: buildRentPayabilityParagraph(rentTableInline.isTable),
      // SUMMARY OF AMOUNTS DUE (docs/move-in-payment-plan-plan.md): one list
      // ("Due on or before move-in, {date}") when the lease starts on or before
      // its document date, else "Due upon signing this Agreement" + the move-in
      // list; then the total bar and the plan sentence (empty with no plan).
      amounts_due_sections: buildAmountsDueSections(amountsDue),
      total_due_grand_total: buildTotalDueGrandTotal(amountsDue.totalCents),
      payment_plan_summary_paragraph: buildPaymentPlanSummaryParagraph(amountsDue),
      // MOVE-IN PAYMENT PLAN parts (payment_plan.md; empty with no plan).
      payment_plan_inclusions: planParts.inclusions,
      payment_plan_schedule_intro: planParts.scheduleIntro,
      payment_plan_schedule_rows: planParts.scheduleRows,
      payment_plan_closing: planParts.closing,
      // MOVE-IN FEES body (move_in_fees.md; empty with no one-time fee).
      move_in_fees_body: moveInFeesBody,
      // §7.4.1: pre-rendered Approved Animals table — STATIC TEXT, one row per
      // pet from the AppFolio Pets snapshot (no DocuSeal fillable tags). Empty
      // when no animals (animals_permitted.md is not compiled in then anyway,
      // so the empty string never reaches HTML — defensive default per the
      // placeholder-guard contract in render_lease.py).
      approved_animals_table: animalsPermitted ? buildApprovedAnimalsTable(s.pets ?? []) : '',
      // RRA v2.2 part B (WEL-180): the pending-decision clause, right after the
      // Approved Animals list (animals_permitted.md). '' unless staff marked an
      // animal `animalsPendingDecision` — every existing lease is byte-identical.
      animal_decision_pending_paragraph: animalsPermitted ? buildAnimalDecisionPendingParagraph(s) : '',
      // RESIDENT DESIGNATION inner table (pre-lease intake wiring). When a
      // designation was collected (intake or staff), renders the designee
      // details read-only; otherwise renders the original blank DocuSeal fill
      // fields byte-identically. Reuses the existing .designee-fields CSS.
      resident_designation_details: buildResidentDesignationDetails(s.residentDesignation),
      // RESIDENT DESIGNATION intro/closing — count-conditional (RCW 59.18.590).
      // Single-resident = original wording (byte-identical); multi-resident scopes
      // it to "no surviving co-Resident". Intro inlines resident_names /
      // premises_address → it's in UNTRUSTED_PREFILL_KEYS.
      resident_designation_intro: buildResidentDesignationIntro(
        residentNames,
        premisesAddress,
        includedNames.length,
      ),
      resident_designation_closing: buildResidentDesignationClosing(includedNames.length),
      // §YARD CARE arrangement (resident-responsible): server-built so the resident
      // fills nothing at signing — silent when they maintain it (default), one
      // sentence when they elected to have Lessor arrange + bill (WEL-17).
      yard_care_arrangement: buildYardCareArrangement(s.yardCareArrangement),
      // Spec §24.3 / §30 builder-constraint 4 (ANIM-03): the RA-pending computed
      // slot was removed. Reasonable-accommodation suspension behavior moves
      // entirely to WHP accommodation policy / AppFolio workflow, so the lease no
      // longer renders it. (WEL-104: the `animalRaPending` / `animalRaCount` staff
      // fields + their builder checkbox were removed too — RA handling is internal
      // per Rule H, tracked by a separate RRA workflow.)
      // The full bespoke §UTILITIES body (grouped-by-arrangement statements +
      // only the general clauses the present arrangements trigger).
      utilities_body: utilitiesBody,
      // Transitional back-compat (mirror of API): emit the old subsection
      // placeholders too so a briefly-stale lease-content markdown still resolves
      // during a container-first deploy. Remove once the new framework markdown
      // is live everywhere.
      utilities_responsibility_subsection: utilitiesBody,
      utilities_billing_subsection: '',
      utilities_shared_subsection: '',
      // §10.2: closing reference sentence pointing to UTILITY CONTACTS;
      // empty string when no utility is tenant_direct (no contacts to point
      // to). Same gate as utility_contacts.md inclusion → never a dangling
      // pointer.
      utility_contacts_reference: utilityContactsReference,
      // §10.3: pre-rendered two-column UTILITY CONTACTS table. Empty string
      // when section is suppressed (include_utility_contacts === false).
      utility_contacts_table: contacts.table,
      // §10.2 mapping: per-utility responsibility cell values (Lessor or
      // Resident). Redundant with utility_responsibility_block but kept as
      // raw scalars in case future content references them.
      water_responsibility: utilityResponsibility(
        s.utilities.find((u) => u.name === 'Water')?.state ?? 'tenant_direct',
      ),
      sewer_responsibility: utilityResponsibility(
        s.utilities.find((u) => u.name === 'Sewer')?.state ?? 'tenant_direct',
      ),
      garbage_responsibility: utilityResponsibility(
        s.utilities.find((u) => u.name === 'Garbage')?.state ?? 'tenant_direct',
      ),
      electricity_responsibility: utilityResponsibility(
        s.utilities.find((u) => u.name === 'Electricity')?.state ?? 'tenant_direct',
      ),
      gas_responsibility: utilityResponsibility(
        s.utilities.find((u) => u.name === 'Gas')?.state ?? 'tenant_direct',
      ),
      // WEL-61: the rendered count must cover the assigned spaces — never fewer
      // than the number of (non-blank) assigned space labels, so a 2nd space can't
      // read "1 parking space(s) … 12, 14". Only ever bumps; existing one-space
      // drafts are unchanged. (Codex review.)
      parking_spots: String(
        Math.max(
          s.parkingSpots,
          [s.parkingAssignedLabel, s.parkingAssignedLabel2].filter((l) => (l ?? '').trim()).length,
        ),
      ),
      // WEL-61: includes the optional second space when set; else == label1.
      assigned_parking: combineAssignedParking(s.parkingAssignedLabel, s.parkingAssignedLabel2),
      parking_notes: s.parkingNotes,
      other_terms_section: buildOtherTermsSection(s),
      safety_section_title:
        s.propertyType === 'mfr'
          ? 'SMOKE DETECTION DEVICES AND CARBON MONOXIDE ALARMS / FIRE SAFETY AND PROTECTION INFORMATION'
          : 'SMOKE DETECTION DEVICES AND CARBON MONOXIDE ALARMS',
      // meeting-2026-05-13 §6.2: Fire Safety Notice now accepts the
      // application group so it can append a detector power-source bullet
      // for MFR leases when AF's `Detectors` OptionValue is set.
      fire_safety_notice_section: buildFireSafetyNoticeSection(s, g),
      // meeting-2026-05-13 §6.2: §9 Smoke/CO appended sentence (MFR-only;
      // empty for SFR or when Detectors OptionValue is unset).
      detector_power_source_disclosure: buildDetectorPowerSourceDisclosure(g, s),
      lead_presence_explanation: '',
      lead_records_documents: '',
      // EPA "Protect Your Family from Lead in Your Home" pamphlet — delivered
      // by link per the LBP electronic-delivery consent (40 CFR 745.107).
      // Verified live 2026-05-24; revisit link-rot guard / self-hosting.
      lead_pamphlet_url:
        'https://www.epa.gov/system/files/documents/2026-02/protectyourfamily_pamphlet_2026_3.pdf',
      // mold_epa_guide_url / mold_wa_doh_url removed — exhibit_mold_moisture.md
      // now embeds DOH 333-302 verbatim and consumes no mold URL vars.
    },
  };
}

// ---------------------------------------------------------------------------
// D2: hasHoa derivation rule (§8.3 source-of-truth rule)
// ---------------------------------------------------------------------------

/**
 * Derive `hasHoa` from `hoaSource` (§8.3).
 * `hoaSource` is the canonical field; `hasHoa` is a cached derived value
 * stored in the form state so `buildRenderConfig` doesn't branch on `hoaSource`.
 *
 * Usage: call this whenever `hoaSource` changes to keep `hasHoa` in sync.
 *
 *   setState(prev => ({ ...prev, hoaSource: newSource, hasHoa: deriveHasHoa(newSource) }));
 */
export function deriveHasHoa(hoaSource: LeaseFormState['hoaSource']): boolean {
  // Phase D Codex N1: whitelist the affirmative values. The previous
  // `!== 'none'` returned true for undefined / legacy drafts saved before
  // hoaSource existed, silently flipping include_hoa on.
  // Meeting 2026-05-14: 'one-off' variant removed.
  return hoaSource === 'property-associated';
}
